import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { getValidEnrollment } from '../utils/enrollmentUtils';
import TaxonomySelector from '../components/TaxonomySelector';
import Tooltip from '../components/Tooltip';
import AssignmentViewer from '../components/AssignmentViewer';
import ApprovalProgress from '../components/ApprovalProgress';
import { v4 as uuidv4 } from 'uuid';
import { useNavigate, useLocation } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import ProjectComments from '../components/ProjectComments';

const initialSupervisor = {
  firstName: '',
  lastName: '',
  title: '',
  email: '',
  phone: '',
  signature: '',
  code: ''
};

const initialForm = {
  type: 'Field Experience',
  date: '',
  timeStart: '',
  timeEnd: '',
  hours: '',
  description: '',
  concentrations: [],
  otherConcentration: '',
  hasOtherConcentration: false,
  flier: null,
  workdocuments: [],
  supervisor: { ...initialSupervisor },
  tor: '',
  survey: { responses: {}, openAnswers: {} },
  taxonomy: [],
  selectedConcentrations: []
};

const hourTypes = [
  'Theory',
  'Lab',
  'Concentration',
  'Capstone',
  'Field Experience'
];

// === Utility: Compare two arrays for shallow equality ===
const arraysEqual = (arr1, arr2) => {
  if (arr1.length !== arr2.length) return false;
  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i]) return false;
  }
  return true;
};

// === Utility: Migrate concentrations from old array format to new structure ===
const migrateConcentrations = (waiver, concentrations) => {
  if (waiver.selectedConcentrations && Array.isArray(waiver.selectedConcentrations)) {
    return waiver.selectedConcentrations;
  } else if (waiver.concentrations && Array.isArray(waiver.concentrations)) {
    // Migrate from old concentrations array to new structure
    return waiver.concentrations.map(concentrationId => {
      const concentration = concentrations.find(c => c.id === concentrationId);
      return concentration ? {
        id: concentration.id,
        title: concentration.title
      } : null;
    }).filter(Boolean);
  }
  return [];
};

// === Utility: Format field names for display ===
const formatFieldName = (fieldName) => {
  const fieldMap = {
    'type': 'Type',
    'hours': 'Hours',
    'date': 'Date',
    'timestart': 'Start Time',
    'timeend': 'End Time',
    'description': 'Description',
    'concentrations': 'Concentrations',
    'hasotherconcentration': 'Has Other Concentration',
    'otherconcentration': 'Other Concentration',
    'supervisor_firstname': 'Supervisor First Name',
    'supervisor_lastname': 'Supervisor Last Name',
    'supervisor_title': 'Supervisor Title',
    'supervisor_email': 'Supervisor Email',
    'supervisor_phone': 'Supervisor Phone',
    'supervisor_signature': 'Supervisor Signature',
    'tor': 'Teacher of Record',
    'surveydata': 'Survey Data',
    'taxonomy': 'Taxonomy',
    'taxonomy_by_concentration': 'Taxonomy by Concentration',
    'flier': 'Flier',
    'workdocuments': 'Work Documents',
    'status': 'Status',
    'progress': 'Progress'
  };
  return fieldMap[fieldName] || fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// === Utility: Render complex objects in a readable format ===
const renderComplexObject = (value, fieldName, taxonomyData = [], concentrationsData = []) => {
  if (typeof value !== 'object' || value === null) return null;
  
  if (fieldName === 'taxonomy' || fieldName === 'taxonomy_by_concentration') {
    return (
      <div style={{ marginTop: '8px' }}>
        {value.map((item, index) => (
          <div key={index} style={{
            marginBottom: '8px',
            padding: '8px',
            backgroundColor: '#f8fafc',
            borderRadius: '6px',
            border: '1px solid #e2e8f0'
          }}>
            {typeof item === 'object' && item !== null ? (
              <div>
                {/* New format */}
                {item.parent && item.taxonomy && item.concentration && (
                  <div>
                    <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: '4px' }}>
                      Taxonomy: {taxonomyData.find(t => t.id === item.taxonomy)?.title || item.taxonomy}
                    </div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>
                      Parent: {item.parent}
                    </div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>
                      Concentration: {concentrationsData.find(c => c.id === item.concentration)?.title || item.concentration}
                    </div>
                  </div>
                )}
                {/* Old format */}
                {item.taxonomy_ids && item.concentration_id && (
                  <div>
                    <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: '4px' }}>
                      Concentration: {concentrationsData.find(c => c.id === item.concentration_id)?.title || item.concentration_id}
                    </div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>
                      Taxonomies: {item.taxonomy_ids.map(tId => 
                        taxonomyData.find(t => t.id === tId)?.title || tId
                      ).join(', ')}
                    </div>
                  </div>
                )}
                {/* Intermediate format */}
                {item.parent_id && item.taxonomy_id && item.concentration && (
                  <div>
                    <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: '4px' }}>
                      Taxonomy: {taxonomyData.find(t => t.id === item.taxonomy_id)?.title || item.taxonomy_id}
                    </div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>
                      Parent: {item.parent_id}
                    </div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>
                      Concentration: {concentrationsData.find(c => c.id === item.concentration)?.title || item.concentration}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                {JSON.stringify(item, null, 2)}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }
  
  if (fieldName === 'supervisor') {
    return (
      <div style={{ marginTop: '8px' }}>
        {Object.entries(value).map(([key, val]) => (
          val !== null && val !== undefined && val !== '' && (
            <div key={key} style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '4px 0',
              borderBottom: '1px solid #e2e8f0'
            }}>
              <span style={{ fontWeight: 600, color: '#374151', minWidth: '120px' }}>
                {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
              </span>
              <span style={{ color: '#1f2937', wordBreak: 'break-word' }}>{val}</span>
            </div>
          )
        ))}
      </div>
    );
  }
  
  if (fieldName === 'survey') {
    return (
      <div style={{ marginTop: '8px' }}>
        {Object.entries(value).map(([key, val]) => (
          val !== null && val !== undefined && val !== '' && (
            <div key={key} style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '4px 0',
              borderBottom: '1px solid #e2e8f0'
            }}>
              <span style={{ fontWeight: 600, color: '#374151', minWidth: '120px' }}>
                {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
              </span>
              <span style={{ color: '#1f2937', wordBreak: 'break-word' }}>{val}</span>
            </div>
          )
        ))}
      </div>
    );
  }
  
  return (
    <div style={{ marginTop: '8px' }}>
      <pre style={{
        fontSize: '12px',
        fontFamily: 'monospace',
        backgroundColor: '#f8fafc',
        padding: '8px',
        borderRadius: '4px',
        border: '1px solid #e2e8f0',
        overflow: 'auto',
        maxHeight: '200px'
      }}>
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
};

// === Utility: Format field values for display ===
const formatFieldValue = (value, fieldName, taxonomyData = [], concentrationsData = []) => {
  if (value === null || value === undefined) return 'None';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) {
    if (value.length === 0) return 'None';
    if (fieldName === 'concentrations') {
      return value.map(id => {
        const concentration = concentrationsData.find(c => c.id === id);
        return concentration ? concentration.title : id;
      }).join(', ');
    }
    if (fieldName === 'workdocuments' || fieldName === 'flier') {
      return value.map(v => v.split('/').pop()).join(', ');
    }
    if (fieldName === 'taxonomy' || fieldName === 'taxonomy_by_concentration') {
      return value.map(item => {
        if (typeof item === 'object' && item !== null) {
          // Handle new format with parent, taxonomy, concentration
          if (item.parent && item.taxonomy && item.concentration) {
            const taxonomyItem = taxonomyData.find(t => t.id === item.taxonomy);
            const concentration = concentrationsData.find(c => c.id === item.concentration);
            return `${taxonomyItem ? taxonomyItem.title : item.taxonomy} (${concentration ? concentration.title : item.concentration})`;
          }
          // Handle old format with taxonomy_ids and concentration_id
          if (item.taxonomy_ids && item.concentration_id) {
            const concentration = concentrationsData.find(c => c.id === item.concentration_id);
            const taxonomyTitles = item.taxonomy_ids.map(tId => {
              const taxonomyItem = taxonomyData.find(t => t.id === tId);
              return taxonomyItem ? taxonomyItem.title : tId;
            }).join(', ');
            return `${taxonomyTitles} (${concentration ? concentration.title : item.concentration_id})`;
          }
          // Handle intermediate format with parent_id, taxonomy_id, concentration
          if (item.parent_id && item.taxonomy_id && item.concentration) {
            const taxonomyItem = taxonomyData.find(t => t.id === item.taxonomy_id);
            const concentration = concentrationsData.find(c => c.id === item.concentration);
            return `${taxonomyItem ? taxonomyItem.title : item.taxonomy_id} (${concentration ? concentration.title : item.concentration})`;
          }
        }
        return JSON.stringify(item);
      }).join(', ');
    }
    return value.join(', ');
  }
  if (typeof value === 'object' && value !== null) {
    // Handle complex objects like supervisor, survey, etc.
    if (fieldName === 'supervisor') {
      const parts = [];
      if (value.firstname) parts.push(`First: ${value.firstname}`);
      if (value.lastname) parts.push(`Last: ${value.lastname}`);
      if (value.title) parts.push(`Title: ${value.title}`);
      if (value.email) parts.push(`Email: ${value.email}`);
      if (value.phone) parts.push(`Phone: ${value.phone}`);
      return parts.length > 0 ? parts.join(', ') : JSON.stringify(value);
    }
    if (fieldName === 'survey') {
      const parts = [];
      Object.keys(value).forEach(key => {
        if (value[key] !== null && value[key] !== undefined && value[key] !== '') {
          parts.push(`${key}: ${value[key]}`);
        }
      });
      return parts.length > 0 ? parts.join(', ') : 'No survey data';
    }
    return JSON.stringify(value, null, 2);
  }
  if (typeof value === 'string' && value.length > 100) {
    return value.substring(0, 100) + '...';
  }
  return value.toString();
};

// === Utility: Compute modifications between two waiver data objects ===
const computeModifications = (newData, oldData, form, flierPath, workDocsPaths) => {
  const modifications = [];
  if (!oldData) return modifications;
  if (newData.type !== oldData.type) {
    modifications.push({ field: 'type', currentValue: oldData.type, newValue: newData.type });
  }
  if (newData.hours !== oldData.hours) {
    modifications.push({ field: 'hours', currentValue: oldData.hours, newValue: newData.hours });
  }
  const originalDate = oldData.date ? new Date(oldData.date).toISOString().split('T')[0] : null;
  const newDate = newData.date ? new Date(newData.date).toISOString().split('T')[0] : null;
  if (originalDate !== newDate) {
    modifications.push({ field: 'date', currentValue: oldData.date, newValue: newData.date });
  }
  if (newData.timestart !== oldData.timestart) {
    modifications.push({ field: 'timestart', currentValue: oldData.timestart, newValue: newData.timestart });
  }
  if (newData.timeend !== oldData.timeend) {
    modifications.push({ field: 'timeend', currentValue: oldData.timeend, newValue: newData.timeend });
  }
  if (newData.description !== oldData.description) {
    modifications.push({ field: 'description', currentValue: oldData.description, newValue: newData.description });
  }
  const originalConcentrations = oldData.concentrations || [];
  const newConcentrations = newData.concentrations || [];
  if (originalConcentrations.length !== newConcentrations.length || originalConcentrations.some((c, i) => c !== newConcentrations[i])) {
    modifications.push({ field: 'concentrations', currentValue: originalConcentrations, newValue: newConcentrations });
  }
  
  if (newData.hasotherconcentration !== oldData.hasotherconcentration) {
    modifications.push({ field: 'hasotherconcentration', currentValue: oldData.hasotherconcentration, newValue: newData.hasotherconcentration });
  }
  if (newData.otherconcentration !== oldData.otherconcentration) {
    modifications.push({ field: 'otherconcentration', currentValue: oldData.otherconcentration, newValue: newData.otherconcentration });
  }
  if (newData.supervisor_firstname !== oldData.supervisor_firstname) {
    modifications.push({ field: 'supervisor_firstname', currentValue: oldData.supervisor_firstname, newValue: newData.supervisor_firstname });
  }
  if (newData.supervisor_lastname !== oldData.supervisor_lastname) {
    modifications.push({ field: 'supervisor_lastname', currentValue: oldData.supervisor_lastname, newValue: newData.supervisor_lastname });
  }
  if (newData.supervisor_title !== oldData.supervisor_title) {
    modifications.push({ field: 'supervisor_title', currentValue: oldData.supervisor_title, newValue: newData.supervisor_title });
  }
  if (newData.supervisor_email !== oldData.supervisor_email) {
    modifications.push({ field: 'supervisor_email', currentValue: oldData.supervisor_email, newValue: newData.supervisor_email });
  }
  if (newData.supervisor_phone !== oldData.supervisor_phone) {
    modifications.push({ field: 'supervisor_phone', currentValue: oldData.supervisor_phone, newValue: newData.supervisor_phone });
  }
  const origTor = oldData.tor === '' ? null : oldData.tor;
  const newTor = newData.tor === '' ? null : newData.tor;
  if (origTor !== newTor) {
    modifications.push({ field: 'tor', currentValue: oldData.tor, newValue: newData.tor });
  }
  const originalSurveyData = oldData.surveydata || [];
  const newSurveyData = newData.surveydata || [];
  let surveyChanged = false;
  if (originalSurveyData.length !== newSurveyData.length) {
    surveyChanged = true;
  } else {
    for (let i = 0; i < originalSurveyData.length; i++) {
      const originalQuestion = originalSurveyData[i];
      const newQuestion = newSurveyData[i];
      if (!originalQuestion || !newQuestion) { surveyChanged = true; break; }
      if (originalQuestion._id !== newQuestion._id || originalQuestion.response !== newQuestion.response) {
        surveyChanged = true; break;
      }
    }
  }
  if (surveyChanged) {
    modifications.push({ field: 'surveydata', currentValue: originalSurveyData, newValue: newSurveyData });
  }
  const originalTaxonomy = oldData.taxonomy || [];
  const newTaxonomy = newData.taxonomy || [];
  let taxonomyChanged = false;
  if (originalTaxonomy.length !== newTaxonomy.length) {
    taxonomyChanged = true;
  } else {
    for (let i = 0; i < originalTaxonomy.length; i++) {
      const originalItem = originalTaxonomy[i];
      const newItem = newTaxonomy[i];
      if (!originalItem || !newItem) { taxonomyChanged = true; break; }
      if (originalItem.parent !== newItem.parent || originalItem.taxonomy !== newItem.taxonomy || originalItem.concentration !== newItem.concentration) {
        taxonomyChanged = true; break;
      }
    }
  }
  if (taxonomyChanged) {
    modifications.push({ field: 'taxonomy', currentValue: originalTaxonomy, newValue: newTaxonomy });
  }
  const originalWorkDocs = oldData.workdocuments || [];
  const newWorkDocs = workDocsPaths || [];
  if (form && form.flier && form.flier instanceof File) {
    modifications.push({ field: 'flier', currentValue: oldData.flier, newValue: flierPath, description: 'New flier uploaded' });
  }
  const hasNewWorkDocs = form && form.workdocuments && form.workdocuments.some(file => file instanceof File);
  if (hasNewWorkDocs) {
    modifications.push({ field: 'workdocuments', currentValue: originalWorkDocs, newValue: newWorkDocs, description: 'New work document(s) uploaded' });
  }
  return modifications;
};

const RequestFEHoursByExternalActivity = ({ userData, setUserData }) => {
  const [form, setForm] = useState(initialForm);
  const [concentrations, setConcentrations] = useState([]);
  const [selectedConcentrations, setSelectedConcentrations] = useState([]);
  const [taxonomyItems, setTaxonomyItems] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showHourTypeInfo, setShowHourTypeInfo] = useState(false);
  const [hourTypeDescriptions, setHourTypeDescriptions] = useState('');
  const [showComments, setShowComments] = useState(false);
  
  // New state for role validation
  const [roleValidationError, setRoleValidationError] = useState(null);
  const [isStudentRole, setIsStudentRole] = useState(false);
  const [showRolePopup, setShowRolePopup] = useState(false);
  
  // New state for supervisor confirmation popup
  const [showSupervisorConfirm, setShowSupervisorConfirm] = useState(false);
  const [supervisorInfo, setSupervisorInfo] = useState(null);
  const [supervisorCode, setSupervisorCode] = useState('');
  const [supervisorCodeError, setSupervisorCodeError] = useState('');
  
  const navigate = useNavigate();
  const location = useLocation();
  
  // Authentication check - redirect to login if no userData
  useEffect(() => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams(location.search);
    const command = params.get('command');
    
    // Allow supervisor command access without authentication, but only if not already logged in
    if (command === 'supervisor' && !userData && !token) {
      // Set fake token first to enable API access
      const fakeToken = 'supervisor-guest-token';
      localStorage.setItem('token', fakeToken);
      setAuthChecking(false);
      return;
    }
    
    if (!userData && !token) {
      // Preserve the current URL for redirect after login
      const returnTo = location.pathname + location.search + location.hash;
      navigate('/login', { state: { from: returnTo } });
      return;
    }
    setAuthChecking(false);
  }, [userData, navigate, location.pathname, location.search, location.hash]);
  
  // Role validation logic - check at component mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const command = params.get('command');
    
    // Special handling for supervisor command - don't validate role until waiver data is loaded
    if (command === 'supervisor') {
      setIsStudentRole(true);
      setRoleValidationError(null);
      return;
    }
    
    // Only perform validation if no command parameter (creating new waiver)
    if (!command) {
      // Check if default access card exists and has student role
      const defaultAccessCard = userData && userData.accesscards && userData.default_accesscard_id
        ? userData.accesscards.find(ac => ac.accesscard_id === userData.default_accesscard_id)
        : null;
      
      if (!defaultAccessCard) {
        setRoleValidationError('No default access card found. Please contact support.');
        return;
      }
      
      if (defaultAccessCard.role !== 'Student') {
        setRoleValidationError('Only students may submit hour waiver requests.');
        setIsStudentRole(false);
        setShowRolePopup(true);
        // Auto-close popup after 3 seconds
        setTimeout(() => {
          setShowRolePopup(false);
        }, 3000);
        return;
      }
      
      // Check if student has valid enrollment
      const validEnrollment = getValidEnrollment(userData);
      if (!validEnrollment) {
        setRoleValidationError('You are a student, but do not have valid enrollment so cannot submit');
        setIsStudentRole(true);
        setShowRolePopup(true);
        // Auto-close popup after 3 seconds
        setTimeout(() => {
          setShowRolePopup(false);
        }, 3000);
        return;
      }
      
      setIsStudentRole(true);
      setRoleValidationError(null);
    } else {
      // If command parameter exists, allow access (viewing existing waiver)
      setIsStudentRole(true);
      setRoleValidationError(null);
    }
  }, [userData, location.search, location.pathname, location.hash]);
  

  const [torOptions, setTorOptions] = useState([{ value: '', label: 'Select...' }]);
  const [systemAssignment, setSystemAssignment] = useState(null);
  const [surveyLoading, setSurveyLoading] = useState(true);
  const assignmentViewerRef = useRef();
  /** Auto-pick enrollment TEACHER_OF_RECORD once per enrollment (avoid clobbering if user clears TOR). */
  const torDefaultFromEnrollmentRef = useRef(null);
  const [finalSubmittedData, setFinalSubmittedData] = useState(null);
  const [showOverlay, setShowOverlay] = useState(false);
  const [overlayMessage, setOverlayMessage] = useState('');
  const [overlaySuccess, setOverlaySuccess] = useState(false);
  const [studentView, setStudentView] = useState(false);
  const [loadingStudentWaiver, setLoadingStudentWaiver] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState('student');
  const [waiverId, setWaiverId] = useState(null); // Track the waiver ID for updates
  const [originalWaiverData, setOriginalWaiverData] = useState(null); // Store original data for comparison
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false); // Track if there are unsaved changes
  const [requiredSteps, setRequiredSteps] = useState([
    {
      step: 1,
      decider: "student",
      title: "Student Submission"
    },
    {
      step: 2,
      decider: "supervisor",
      title: "Supervisor Approval"
    },
    {
      step: 3,
      decider: "schoolAdmin",
      title: "School Admin Approval"
    },
    {
      step: 4,
      decider: "systemAdmin",
      title: "System Admin Approval"
    }
  ]);

  const [signatureError, setSignatureError] = useState('');
  const signaturePadRef = useRef();
  const [loadedStudentInfo, setLoadedStudentInfo] = useState(null);
  const [signaturePadHidden, setSignaturePadHidden] = useState(false);
  const [studentInfoError, setStudentInfoError] = useState(null);
  const [criticalError, setCriticalError] = useState(null);
  const [criticalErrorTitle, setCriticalErrorTitle] = useState('Critical Error');
  const [criticalErrorPath, setCriticalErrorPath] = useState('/');
  const [criticalErrorButtonText, setCriticalErrorButtonText] = useState('Go to Home Page');
  const [criticalErrorShowButton, setCriticalErrorShowButton] = useState(true);
  const [concentrationsError, setConcentrationsError] = useState(null);
  const [showRevisionsModal, setShowRevisionsModal] = useState(false);
  const [expandedModifications, setExpandedModifications] = useState({});
  
  // Helper function to set critical error with custom title, navigation path, button text, and show/hide button
  const setCriticalErrorWithOptions = (message, title = 'Critical Error', path = '/', buttonText = 'Go to Home Page', showButton = true) => {
    setCriticalError(message);
    setCriticalErrorTitle(title);
    setCriticalErrorPath(path);
    setCriticalErrorButtonText(buttonText);
    setCriticalErrorShowButton(showButton);
  };
  
  // Helper function to set critical error with reload action
  const setCriticalErrorWithReload = (message, title = 'System Error') => {
    setCriticalError(message);
    setCriticalErrorTitle(title);
    setCriticalErrorPath('reload'); // Special flag for reload
    setCriticalErrorButtonText('Reload Page');
    setCriticalErrorShowButton(true);
  };
  const [assignmentError, setAssignmentError] = useState(null);
  const [waiverError, setWaiverError] = useState(null);
  const [authChecking, setAuthChecking] = useState(true);

  // Function to fetch taxonomy data by IDs
  const fetchTaxonomyData = async (taxonomyIds) => {
    if (!taxonomyIds || taxonomyIds.length === 0) return [];
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/taxonomy/batch', { ids: taxonomyIds }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data.taxonomy || [];
    } catch (err) {
      console.error('Error fetching taxonomy data:', err);
      return [];
    }
  };

  // Function to fetch all taxonomy data
  const fetchAllTaxonomyData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/taxonomy/all', {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data.items || [];
    } catch (err) {
      console.error('Error fetching all taxonomy data:', err);
      return [];
    }
  };

  // Compute defaultAccessCard and validEnrollment
  const defaultAccessCard = userData && userData.accesscards && userData.default_accesscard_id
    ? userData.accesscards.find(ac => ac.accesscard_id === userData.default_accesscard_id)
    : null;
  const validEnrollment = getValidEnrollment(userData);
  const showTOR = validEnrollment && validEnrollment.association && validEnrollment.association.displayname === 'FA04';
  // Compute display string - updated to use loadedStudentInfo when viewing by ID
  const studentDisplay = studentView && loadedStudentInfo
    ? `${loadedStudentInfo.account?.first_name || ''} ${loadedStudentInfo.account?.last_name || ''} (${loadedStudentInfo.accesscard?.email || ''})`
    : (userData && defaultAccessCard && validEnrollment
    ? `${userData.first_name || ''} ${userData.last_name || ''} (${defaultAccessCard.email || ''})`
        : 'Student Name (email)');
  
  const gradeSchoolDisplay = studentView && loadedStudentInfo
    ? `${loadedStudentInfo.enrollment?.gradelevel || ''} grade at ${loadedStudentInfo.school?.title || ''}${loadedStudentInfo.association?.displayname ? ` (${loadedStudentInfo.association.displayname})` : ''}`
    : (userData && defaultAccessCard && validEnrollment
        ? `${validEnrollment.gradelevel || ''} grade at ${defaultAccessCard.school?.title || ''}${validEnrollment.association?.displayname ? ` (${validEnrollment.association.displayname})` : ''}`
        : 'Grade at School (Association)');

  useEffect(() => {
    const token = localStorage.getItem('token');
    
    // Fetch concentrations
    axios.get('/api/concentrations', {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => {
        setConcentrations(res.data.concentrations || []);
        setConcentrationsError(null);
      })
      .catch((err) => {
        console.error('Error fetching concentrations:', err);
        setConcentrations([]);
        setConcentrationsError('Failed to load concentrations');
        setCriticalErrorWithReload('Failed to load page properly. Please refresh and try again.');
      });

    // Fetch taxonomy items
    fetchAllTaxonomyData()
      .then(taxonomyData => {
        setTaxonomyItems(taxonomyData);
      })
      .catch((err) => {
        console.error('Error fetching taxonomy items:', err);
        setTaxonomyItems([]);
      });
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get('/api/miscellaneous?title=' + encodeURIComponent('Project Types Descriptions'), {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => {
        if (res.data && res.data.data && res.data.data.length > 0) {
          setHourTypeDescriptions(res.data.data[0]);
        }
      })
      .catch(() => setHourTypeDescriptions(''));
  }, []);

  // Teacher of Record dropdown: prefer TEACHER_OF_RECORD on the student's enrollment (relationships);
  // if none, fall back to all instructors linked to the FA association (e.g. FA04).
  useEffect(() => {
    if (
      !showTOR ||
      !validEnrollment ||
      !validEnrollment.association ||
      !validEnrollment.association.id
    ) {
      setTorOptions([{ value: '', label: 'Select...' }]);
      return;
    }

    const associationId = validEnrollment.association.id;
    const token = localStorage.getItem('token');

    const parseEnrollmentTors = (en) => {
      if (!en) return [];
      if (Array.isArray(en.tors)) return en.tors.filter((t) => t && t.account_id);
      if (typeof en.tors === 'string') {
        try {
          const parsed = JSON.parse(en.tors || '[]');
          return Array.isArray(parsed) ? parsed.filter((t) => t && t.account_id) : [];
        } catch {
          return [];
        }
      }
      return [];
    };

    const fetchTORs = async () => {
      try {
        const enrollmentTors = parseEnrollmentTors(validEnrollment);
        if (enrollmentTors.length > 0) {
          const torList = enrollmentTors.map((t) => {
            const fn = t.first_name || '';
            const ln = t.last_name || '';
            const em = t.email || '';
            const label =
              (fn || ln || em)
                ? `${fn} ${ln}`.trim() + (em ? ` (${em})` : '')
                : `Teacher of Record (${String(t.account_id).slice(0, 8)}…)`;
            return { value: t.account_id, label };
          });
          torList.sort((a, b) => a.label.localeCompare(b.label));
          setTorOptions([{ value: '', label: 'Select...' }, ...torList]);
          if (torDefaultFromEnrollmentRef.current !== validEnrollment.enrollment_id) {
            torDefaultFromEnrollmentRef.current = validEnrollment.enrollment_id;
            const firstId = enrollmentTors[0].account_id;
            setForm((f) => (f.tor ? f : { ...f, tor: firstId }));
          }
          return;
        }

        torDefaultFromEnrollmentRef.current = null;

        const response = await axios.get('/api/instructorconcentrations', {
          headers: { Authorization: `Bearer ${token}` },
          params: { associationId }
        });
        const torList = (response.data || []).map((i) => ({
          value: i.account._id,
          label: `${i.account.firstName} ${i.account.lastName} (${i.account.email})`
        }));
        setTorOptions([
          { value: '', label: 'Select...' },
          ...torList.sort((a, b) => a.label.localeCompare(b.label))
        ]);
      } catch (err) {
        console.error('Error loading Teacher of Record options:', err);
        setTorOptions([{ value: '', label: 'Select...' }]);
      }
    };

    fetchTORs();
  }, [
    showTOR,
    validEnrollment && validEnrollment.association && validEnrollment.association.id,
    validEnrollment && validEnrollment.enrollment_id,
    validEnrollment && validEnrollment.tors
  ]);

  // Fetch the system assignment for survey
  const fetchSystemAssignment = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/api/assignments/system?title=Survey%20Portion%20(Hours%20Request)', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSystemAssignment(res.data.assignment || null);
      setAssignmentError(null);
      return res.data.assignment || null;
    } catch (err) {
      console.error('Error fetching system assignment:', err);
      setSystemAssignment(null);
      setAssignmentError('Failed to load survey assignment');
      setCriticalErrorWithReload('Failed to load page properly. Please refresh and try again.');
      return null;
    } finally {
      setSurveyLoading(false);
    }
  };

  useEffect(() => {
    fetchSystemAssignment();
  }, []);

  // Check for changes whenever form data changes
  useEffect(() => {
    checkForChanges();
  }, [form, originalWaiverData, waiverId]);

  // Student view: check for command=student&id=...
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const command = params.get('command');
    const waiverId = params.get('id');
    
    // Validate command parameter if present
    if (command) {
      const validCommands = ['student', 'schoolAdmin', 'systemAdmin', 'tor', 'supervisor'];
      if (!validCommands.includes(command)) {
        setCriticalErrorWithOptions('Invalid command parameter', 'Access Denied', '/');
        return;
      }
      
      // Check if id parameter is present when command is provided
      if (!waiverId) {
        setCriticalErrorWithOptions('Missing waiver ID parameter. Please provide a valid waiver ID.', 'Access Denied', '/');
        return;
      }
    }
    
    if (command && waiverId) {
      setStudentView(true);
      setCurrentUserRole(command);
      setLoadingStudentWaiver(true);
      // Fetch the hourwaiver by id
      const fetchWaiver = async () => {
        try {
          const token = localStorage.getItem('token');
          const res = await axios.get(`/api/hourwaivers/${waiverId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const waiver = res.data.hourwaiver;
          const enrollmentTeacherOfRecordAccountIds = Array.isArray(
            res.data.enrollmentTeacherOfRecordAccountIds
          )
            ? res.data.enrollmentTeacherOfRecordAccountIds
            : [];
          if (waiver) {
            console.log('Loading waiver data:', waiver);
            console.log('Flier:', waiver.flier);
            console.log('Work documents:', waiver.workdocuments);
            console.log('Survey data:', waiver.surveydata);
            
            // Set the waiver ID for updates
            setWaiverId(waiver.id);
            
            // Store original waiver data for comparison
            setOriginalWaiverData(waiver);
            
            // If this is a supervisor command, handle verification
            if (command === 'supervisor' && waiver.supervisor_email) {
              // First check if user has fake email (not logged in)
              if (userData && userData.primary_email === 'supervisor@guest.com') {
                // User is NOT logged in - show confirmation popup requiring supervisor code
                if (waiver.supervisor_firstname && waiver.supervisor_lastname) {
                  setSupervisorInfo({
                    firstName: waiver.supervisor_firstname,
                    lastName: waiver.supervisor_lastname,
                    email: waiver.supervisor_email
                  });
                  setShowSupervisorConfirm(true);
                }
              } else if (!userData || !userData.primary_email) {
                // No user data or email - show supervisor confirmation popup for guest access
                if (waiver.supervisor_firstname && waiver.supervisor_lastname) {
                  setSupervisorInfo({
                    firstName: waiver.supervisor_firstname,
                    lastName: waiver.supervisor_lastname,
                    email: waiver.supervisor_email
                  });
                  setShowSupervisorConfirm(true);
                }
              } else {
                // User IS logged in - verify email matches
                if (userData && userData.primary_email) {
                  // Check both primary_email and default access card email
                  const userEmail = userData.primary_email;
                  const defaultAccessCardEmail = userData.accesscards && userData.default_accesscard_id
                    ? userData.accesscards.find(ac => ac.accesscard_id === userData.default_accesscard_id)?.email
                    : null;
                  
                  const emailMatches = (userEmail && userEmail.toLowerCase() === waiver.supervisor_email.toLowerCase()) ||
                                     (defaultAccessCardEmail && defaultAccessCardEmail.toLowerCase() === waiver.supervisor_email.toLowerCase());
                  
                  if (!emailMatches) {
                    console.error('Supervisor email verification failed:', {
                      currentUserEmail: userEmail,
                      defaultAccessCardEmail: defaultAccessCardEmail,
                      waiverSupervisorEmail: waiver.supervisor_email
                    });
                    setCriticalErrorWithOptions('Your current account email does not match the supervisor email for this form.', 'Access Denied', '/');
                    return;
                  }
                  // Email matches - grant access immediately (no supervisor code needed)
                  console.log('Supervisor email verified - granting access');
                }
              }
            }
            
            // If this is a schoolAdmin command, handle verification
            if (command === 'schoolAdmin'||command === 'systemAdmin') {
              if (!userData || !userData.accesscards || !userData.default_accesscard_id) {
                setCriticalErrorWithOptions('Unable to verify school admin identity. Please log in with the correct admin account.', 'Access Denied', '/');
                return;
              }
              
              const defaultAccessCard = userData.accesscards.find(ac => ac.accesscard_id === userData.default_accesscard_id);
              if (!defaultAccessCard) {
                setCriticalErrorWithOptions('No default access card found. Please contact support.', 'Access Denied', '/');
                return;
              }
              
              // Check if user's role includes "Admin" (could be "Admin", "Super - Admin", "System Admin", or "Super - System Admin")
              if (!defaultAccessCard.role || !defaultAccessCard.role.includes('Admin')) {
                console.error('School admin role verification failed:', {
                  currentUserRole: defaultAccessCard.role,
                  requiredRole: 'Admin, Super - Admin, System Admin, or Super - System Admin'
                });
                setCriticalErrorWithOptions('Your current account does not have school admin privileges.', 'Access Denied', '/');
                return;
              }
              
              // Verify school access - school admin should have access to the student's school
              if (waiver.enrollment && loadedStudentInfo) {
                const studentSchoolId = loadedStudentInfo.school?.school_id;
                const adminSchoolId = defaultAccessCard.school_id;
                
                if (studentSchoolId && adminSchoolId && studentSchoolId !== adminSchoolId) {
                  console.error('School admin school verification failed:', {
                    adminSchoolId: adminSchoolId,
                    studentSchoolId: studentSchoolId
                  });
                  setCriticalErrorWithOptions('You can only approve waivers for students at your school.', 'Access Denied', '/');
                  return;
                }
              }
              
              console.log('School admin verification successful');
            }
            
            // If this is a tor command, handle verification
            if (command === 'tor') {
              if (!userData || !userData.account_id) {
                setCriticalErrorWithOptions('Unable to verify teacher of record identity. Please log in with the correct account.', 'Access Denied', '/');
                return;
              }
              const matchesWaiverTor = waiver.tor && userData.account_id === waiver.tor;
              const matchesEnrollmentTor = enrollmentTeacherOfRecordAccountIds.includes(
                userData.account_id
              );
              if (!matchesWaiverTor && !matchesEnrollmentTor) {
                console.error('TOR verification failed:', {
                  currentUserAccountId: userData.account_id,
                  waiverTorAccountId: waiver.tor,
                  enrollmentTeacherOfRecordAccountIds
                });
                setCriticalErrorWithOptions(
                  "Your current account is not recognized as a teacher of record for this student's enrollment or the TOR selected on this waiver.",
                  'Access Denied',
                  '/'
                );
                return;
              }
              console.log('TOR verification successful');
            }
            
            // Fetch full student info if we have an enrollment
            if (waiver.enrollment) {
              try {
                const studentInfoRes = await axios.get(`/api/enrollments/${waiver.enrollment}/full-student-info`, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                setLoadedStudentInfo(studentInfoRes.data);
                setStudentInfoError(null);
                
                // Verify student identity if command=student
                if (command === 'student' && userData && userData.account_id) {
                  const studentAccountId = studentInfoRes.data.account?.account_id;
                  if (studentAccountId && userData.account_id !== studentAccountId) {
                    console.error('Student identity verification failed:', {
                      currentUserAccountId: userData.account_id,
                      waiverStudentAccountId: studentAccountId
                    });
                    setCriticalErrorWithOptions('Your current account is not recognized as this forms student.', 'Access Denied', '/');
                    return;
                  }
                }
              } catch (err) {
                console.error('Error fetching student info:', err);
                setLoadedStudentInfo(null);
                setStudentInfoError('Failed to load student information. Please try again.');
                setCriticalErrorWithReload('Failed to load page properly. Please refresh and try again.');
              }
            } else {
              // No enrollment found - this shouldn't happen for valid waivers
              if (command === 'student') {
                setCriticalErrorWithOptions('Unable to verify student identity. This waiver appears to be invalid or corrupted.', 'Access Denied', '/');
                return;
              }
            }
            
            // Populate form fields from waiver
            setForm({
              ...initialForm,
              ...waiver,
              status: waiver.status || 'Submitted', // Explicitly preserve status
              date: new Date(waiver.date).toISOString().split('T')[0] || '',
              timeStart: waiver.timestart || '',
              timeEnd: waiver.timeend || '',
              concentrations: waiver.concentrations || [],
              workdocuments: waiver.workdocuments || [],
              flier: waiver.flier || null,
              hasOtherConcentration: waiver.hasotherconcentration || false,
              otherConcentration: waiver.otherconcentration || '',
              supervisor: {
                firstName: waiver.supervisor_firstname || '',
                lastName: waiver.supervisor_lastname || '',
                title: waiver.supervisor_title || '',
                email: waiver.supervisor_email || '',
                phone: waiver.supervisor_phone || '',
                signature: waiver.supervisor_signature || '',
                code: waiver.supervisor_code || ''
              },
              tor: waiver.tor || '',
              survey: waiver.surveydata || [],
              progress: waiver.progress || [],
              taxonomy: waiver.taxonomy || [],
              selectedConcentrations: waiver.selectedConcentrations || []
            });

            // Initialize selectedConcentrations from the loaded data with migration
            const migratedConcentrations = migrateConcentrations(waiver, concentrations);
            setSelectedConcentrations(migratedConcentrations);
            
            // Also ensure form.concentrations has the concentration IDs
            if (waiver.concentrations && Array.isArray(waiver.concentrations)) {
              setForm(f => ({ ...f, concentrations: waiver.concentrations }));
            } else if (migratedConcentrations.length > 0) {
              setForm(f => ({ ...f, concentrations: migratedConcentrations.map(c => c.id) }));
            }
          } else {
            setCriticalErrorWithOptions(`Waiver with ID "${waiverId}" was not found. Please check the URL and try again.`, 'Access Denied', '/');
            return; // Exit early to prevent setting form with null data
          }
        } catch (err) {
          console.error('Error fetching waiver:', err);
          
          // Check if it's a 404 error (waiver not found)
          if (err.response && err.response.status === 404) {
            setCriticalErrorWithOptions(`Waiver with ID "${waiverId}" was not found. Please check the URL and try again.`, 'Access Denied', '/');
          } else if (err.response && err.response.status === 500) {
            // Server error - could be database connection issue
            setCriticalErrorWithReload('Server error occurred while loading the waiver. Please try again later.');
          } else {
            // For other errors (network, etc.), show the generic reload message
            setCriticalErrorWithReload('Failed to load page properly. Please refresh and try again.');
          }
        } finally {
          setLoadingStudentWaiver(false);
        }
      };
      fetchWaiver();
    }
  }, [location.search]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    
    if (name.startsWith('supervisor.')) {
      setForm(f => ({
        ...f,
        supervisor: {
          ...f.supervisor,
          [name.replace('supervisor.', '')]: value
        }
      }));
    } else if (name.startsWith('survey.')) {
      setForm(f => ({
        ...f,
        survey: {
          ...f.survey,
          [name.replace('survey.', '')]: value
        }
      }));
    } else if (type === 'checkbox') {
      setForm(f => {
        const newForm = { 
          ...f, 
          [name]: checked,
          // Clear otherConcentration when hasOtherConcentration is unchecked
          ...(name === 'hasOtherConcentration' && !checked && { otherConcentration: '' })
        };
        
        
        return newForm;
      });
    } else {
      setForm(f => {
        const newForm = { ...f, [name]: value };
        
        
        return newForm;
      });
    }
  };



  const getTooltipContent = (item) => {
    let content = '';
    if (item.title) {
      content += `Title: ${item.title}`;
    }
    if (item.description) {
      if (content) content += '\n\n';
      content += `Description: ${item.description}`;
    }
    if (!content) {
      content = 'No title or description available';
    }
    return content;
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (name === 'flier') {
      setForm(f => ({ ...f, flier: files[0] }));
    } else if (name === 'workdocuments') {
      setForm(f => ({ ...f, workdocuments: Array.from(files) }));
    }
  };

  const validate = () => {
    const errs = {};
    if (!form.type) errs.type = 'Type is required.';
    if (!form.date) errs.date = 'Date is required.';
    if (!form.timeStart) errs.timeStart = 'Start time is required.';
    if (!form.timeEnd) errs.timeEnd = 'End time is required.';
    if (!form.hours || isNaN(form.hours)) errs.hours = 'Valid hours required.';
    if (!form.description) errs.description = 'Description is required.';
    if (form.concentrations.length === 0 && !form.hasOtherConcentration) errs.concentrations = 'Select at least one concentration or specify Other.';
    if (form.hasOtherConcentration && !form.otherConcentration) errs.otherConcentration = 'Please specify Other concentration.';
    if (!form.workdocuments || form.workdocuments.length === 0) errs.workdocuments = 'Work documents are required.';
    if (!form.flier) errs.flier = 'Flier is required.';
    // Supervisor fields
    const s = form.supervisor;
    if (!s.firstName) errs.supervisorFirstName = 'Supervisor first name required.';
    if (!s.lastName) errs.supervisorLastName = 'Supervisor last name required.';
    if (!s.title) errs.supervisorTitle = 'Supervisor title required.';
    if (!s.email || !/^\S+@\S+\.\S+$/.test(s.email)) errs.supervisorEmail = 'Valid supervisor email required.';
    if (!s.phone) errs.supervisorPhone = 'Supervisor phone required.';
    if (showTOR && !form.tor) errs.tor = 'Teacher of Record is required.';
    // Survey validation will be handled by AssignmentViewer
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSubmitError('');
    setSubmitSuccess(false);
    setFinalSubmittedData(null);
    setShowOverlay(true);
    setOverlayMessage(waiverId ? 'Updating form...' : 'Submitting request...');
    setOverlaySuccess(false);
    
    // Validate survey via AssignmentViewer
    if (assignmentViewerRef.current && typeof assignmentViewerRef.current.validateSurvey === 'function') {
      const surveyError = assignmentViewerRef.current.validateSurvey();
      if (surveyError) {
        setOverlayMessage(surveyError);
        setOverlaySuccess(false);
        setTimeout(() => setShowOverlay(false), 2000);
        return;
      }
    }
    
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      setOverlayMessage('Missing required fields. Please complete all required fields.');
      setOverlaySuccess(false);
      setTimeout(() => setShowOverlay(false), 2000);
      return;
    }
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      let finalData;
      
      if (waiverId) {
        // UPDATE EXISTING WAIVER
        console.log('Updating existing waiver:', waiverId);
        
        // Handle file uploads for updates
        let flierPath = null;
        let workDocsPaths = [];
        
        // Only upload new files if they're actual File objects (not existing URLs)
        if (form.flier && form.flier instanceof File) {
          // Generate a unique upload ID for this flier
          const uploadId = uuidv4();
          
          const ext = form.flier.name.split('.').pop();
          const key = `public/hour-waivers/${waiverId}/flier.${ext}`;
          
          // Get presigned URL for direct S3 upload
          const response = await axios.post('/api/upload-url', {
            filename: form.flier.name,
            contentType: form.flier.type,
            fileSize: form.flier.size,
            uploadId: uploadId,
            details: 'Hour waiver flier update',
            key: key
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          // Upload file directly to S3 using presigned URL
          const uploadResponse = await fetch(response.data.presignedUrl, {
            method: 'PUT',
            body: form.flier,
            headers: {
              'Content-Type': form.flier.type
            }
          });
          
          if (!uploadResponse.ok) {
            throw new Error(`Upload failed: ${uploadResponse.statusText}`);
          }
          
          flierPath = response.data.uploadRecord.link;
        } else {
          // Keep existing flier path
          flierPath = form.flier;
        }
        
      if (form.workdocuments && form.workdocuments.length > 0) {
          for (let i = 0; i < form.workdocuments.length; i++) {
            const file = form.workdocuments[i];
            if (file instanceof File) {
              // Generate a unique upload ID for this work document
              const uploadId = uuidv4();
              
              // Upload new file
              const ext = file.name.split('.').pop();
              const key = `public/hour-waivers/${waiverId}/workDoc_${i}.${ext}`;
              
              // Get presigned URL for direct S3 upload
              const response = await axios.post('/api/upload-url', {
                filename: file.name,
                contentType: file.type,
                fileSize: file.size,
                uploadId: uploadId,
                details: 'Hour waiver work document update',
                key: key
              }, {
                headers: { Authorization: `Bearer ${token}` }
              });
              
              // Upload file directly to S3 using presigned URL
              const uploadResponse = await fetch(response.data.presignedUrl, {
                method: 'PUT',
                body: file,
                headers: {
                  'Content-Type': file.type
                }
              });
              
              if (!uploadResponse.ok) {
                throw new Error(`Upload failed: ${uploadResponse.statusText}`);
              }
              
              workDocsPaths.push(response.data.uploadRecord.link);
            } else {
              // Keep existing file path
              workDocsPaths.push(file);
            }
          }
        }
        
        // Prepare update data
        const updateData = getPreviewData();
        updateData.flier = flierPath;
        updateData.workdocuments = workDocsPaths;
        updateData.revision = form.revision || 0; // Current revision
        updateData.expectedUpdatedDate = form.updateddate; // For conflict detection
        // Only include supervisor_signature if currentUserRole is supervisor and a signature is present
        if (currentUserRole === 'supervisor' && form.supervisor.signature) {
          updateData.supervisor_signature = form.supervisor.signature;
        }
        
        // Track modifications by comparing with original waiver data
        const modifications = [];
        
        if (!originalWaiverData) {
          console.warn('No original waiver data available for comparison');
        } else {
          // Compare form fields with original data
          if (updateData.type !== originalWaiverData.type) {
            modifications.push({
              field: 'type',
              currentValue: originalWaiverData.type,
              newValue: updateData.type
            });
          }
          
          if (updateData.hours !== originalWaiverData.hours) {
            modifications.push({
              field: 'hours',
              currentValue: originalWaiverData.hours,
              newValue: updateData.hours
            });
          }
          
          // Compare dates by converting to same format (YYYY-MM-DD)
          const originalDate = originalWaiverData.date ? new Date(originalWaiverData.date).toISOString().split('T')[0] : null;
          const newDate = updateData.date ? new Date(updateData.date).toISOString().split('T')[0] : null;
          if (originalDate !== newDate) {
            modifications.push({
              field: 'date',
              currentValue: originalWaiverData.date,
              newValue: updateData.date
            });
          }
          
          if (updateData.timestart !== originalWaiverData.timestart) {
            modifications.push({
              field: 'timestart',
              currentValue: originalWaiverData.timestart,
              newValue: updateData.timestart
            });
          }
          
          if (updateData.timeend !== originalWaiverData.timeend) {
            modifications.push({
              field: 'timeend',
              currentValue: originalWaiverData.timeend,
              newValue: updateData.timeend
            });
          }
          
          if (updateData.description !== originalWaiverData.description) {
            modifications.push({
              field: 'description',
              currentValue: originalWaiverData.description,
              newValue: updateData.description
            });
          }
          
          // Compare concentrations arrays
          const originalConcentrations = originalWaiverData.concentrations || [];
          const newConcentrations = updateData.concentrations || [];
          if (originalConcentrations.length !== newConcentrations.length || 
              originalConcentrations.some((c, i) => c !== newConcentrations[i])) {
            modifications.push({
              field: 'concentrations',
              currentValue: originalConcentrations,
              newValue: newConcentrations
            });
          }
          
          // Compare other concentration fields
          if (updateData.hasotherconcentration !== originalWaiverData.hasotherconcentration) {
            modifications.push({
              field: 'hasotherconcentration',
              currentValue: originalWaiverData.hasotherconcentration,
              newValue: updateData.hasotherconcentration
            });
          }
          
          if (updateData.otherconcentration !== originalWaiverData.otherconcentration) {
            modifications.push({
              field: 'otherconcentration',
              currentValue: originalWaiverData.otherconcentration,
              newValue: updateData.otherconcentration
            });
          }
          
          // Compare supervisor data
          if (updateData.supervisor_firstname !== originalWaiverData.supervisor_firstname) {
            modifications.push({
              field: 'supervisor_firstname',
              currentValue: originalWaiverData.supervisor_firstname,
              newValue: updateData.supervisor_firstname
            });
          }
          
          if (updateData.supervisor_lastname !== originalWaiverData.supervisor_lastname) {
            modifications.push({
              field: 'supervisor_lastname',
              currentValue: originalWaiverData.supervisor_lastname,
              newValue: updateData.supervisor_lastname
            });
          }
          
          if (updateData.supervisor_title !== originalWaiverData.supervisor_title) {
            modifications.push({
              field: 'supervisor_title',
              currentValue: originalWaiverData.supervisor_title,
              newValue: updateData.supervisor_title
            });
          }
          
          if (updateData.supervisor_email !== originalWaiverData.supervisor_email) {
            modifications.push({
              field: 'supervisor_email',
              currentValue: originalWaiverData.supervisor_email,
              newValue: updateData.supervisor_email
            });
          }
          
          if (updateData.supervisor_phone !== originalWaiverData.supervisor_phone) {
            modifications.push({
              field: 'supervisor_phone',
              currentValue: originalWaiverData.supervisor_phone,
              newValue: updateData.supervisor_phone
            });
          }
          
          // Special handling for tor: treat null and '' as equivalent (no change)
          const origTor = originalWaiverData.tor === '' ? null : originalWaiverData.tor;
          const newTor = updateData.tor === '' ? null : updateData.tor;
          if (origTor !== newTor) {
            modifications.push({
              field: 'tor',
              currentValue: originalWaiverData.tor,
              newValue: updateData.tor
            });
          }
          
          // Compare survey data by checking individual question responses
          const originalSurveyData = originalWaiverData.surveydata || [];
          const newSurveyData = updateData.surveydata || [];
          
          let surveyChanged = false;
          
          // Check if arrays have different lengths
          if (originalSurveyData.length !== newSurveyData.length) {
            surveyChanged = true;
          } else {
            // Compare each question's response
            for (let i = 0; i < originalSurveyData.length; i++) {
              const originalQuestion = originalSurveyData[i];
              const newQuestion = newSurveyData[i];
              
              if (!originalQuestion || !newQuestion) {
                surveyChanged = true;
                break;
              }
              
              // Compare question IDs and responses
              if (originalQuestion._id !== newQuestion._id || 
                  originalQuestion.response !== newQuestion.response) {
                surveyChanged = true;
                break;
              }
            }
          }
          
          if (surveyChanged) {
            modifications.push({
              field: 'surveydata',
              currentValue: originalSurveyData,
              newValue: newSurveyData
            });
          }
          
          // Compare taxonomy data by checking individual concentration entries
          const originalTaxonomy = originalWaiverData.taxonomy || [];
          const newTaxonomy = updateData.taxonomy || [];
          
          let taxonomyChanged = false;
          
          // Check if arrays have different lengths
          if (originalTaxonomy.length !== newTaxonomy.length) {
            taxonomyChanged = true;
          } else {
            // Compare each concentration's taxonomy data
            for (let i = 0; i < originalTaxonomy.length; i++) {
              const originalConc = originalTaxonomy[i];
              const newConc = newTaxonomy[i];
              
              if (!originalConc || !newConc) {
                taxonomyChanged = true;
                break;
              }
              
              // Compare concentration_id, concentration_title, and taxonomy_ids
              if (originalConc.concentration_id !== newConc.concentration_id ||
                  originalConc.concentration_title !== newConc.concentration_title ||
                  !arraysEqual(originalConc.taxonomy_ids || [], newConc.taxonomy_ids || [])) {
                taxonomyChanged = true;
                break;
              }
            }
          }
          
          if (taxonomyChanged) {
            modifications.push({
              field: 'taxonomy',
              currentValue: originalTaxonomy,
              newValue: newTaxonomy
            });
          }
          
          // Check if new files were uploaded
          const originalWorkDocs = originalWaiverData.workdocuments || [];
          const newWorkDocs = workDocsPaths || [];
          
          // Check if flier was uploaded (new File object means upload)
          if (form.flier && form.flier instanceof File) {
            modifications.push({
              field: 'flier',
              currentValue: originalWaiverData.flier,
              newValue: flierPath,
              description: 'New flier uploaded'
            });
          }
          
          // Check if work documents were uploaded (any File objects in the array)
          const hasNewWorkDocs = form.workdocuments && form.workdocuments.some(file => file instanceof File);
          if (hasNewWorkDocs) {
            modifications.push({
              field: 'workdocuments',
              currentValue: originalWorkDocs,
              newValue: newWorkDocs,
              description: 'New work document(s) uploaded'
            });
          }
        }
        
        // Check if there are any actual changes
        if (modifications.length === 0) {
          setOverlayMessage('No changes detected. Nothing to update.');
          setOverlaySuccess(false);
          setTimeout(() => setShowOverlay(false), 2000);
          return;
        }
        
        // Add to update history with modifications
        const newUpdateEntry = {
          action: "updated",
          modifications: modifications,
          timestamp: new Date().toISOString(),
          who: `${userData?.first_name || ''} ${userData?.last_name || ''} (${userData?.primary_email || ''})`.trim()
        };
        updateData.updatehistory = [
          newUpdateEntry,
          ...(form.updatehistory || [])
        ];
        
        console.log('Sending update data:', updateData);
        console.log('Modifications detected:', modifications.length, modifications.map(m => `${m.field}: ${m.description || 'changed'}`));
        
        // Send PATCH request
        await axios.patch(`/api/hourwaivers/${waiverId}`, updateData, {
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
        });
        
        finalData = { ...updateData, id: waiverId };
        
      } else {
        // CREATE NEW WAIVER
        console.log('Creating new waiver');
        
        // 1. Generate UUID for this hourwaiver
        const hourwaiverId = uuidv4();
        
        // 2. Upload flier if present
        let flierPath = null;
        if (form.flier && form.flier instanceof File) {
          // Generate a unique upload ID for this flier
          const uploadId = uuidv4();
          
          const ext = form.flier.name.split('.').pop();
          const key = `public/hour-waivers/${hourwaiverId}/flier.${ext}`;
          
          // Get presigned URL for direct S3 upload
          const response = await axios.post('/api/upload-url', {
            filename: form.flier.name,
            contentType: form.flier.type,
            fileSize: form.flier.size,
            uploadId: uploadId,
            details: 'Hour waiver flier creation',
            key: key
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          // Upload file directly to S3 using presigned URL
          const uploadResponse = await fetch(response.data.presignedUrl, {
            method: 'PUT',
            body: form.flier,
            headers: {
              'Content-Type': form.flier.type
            }
          });
          
          if (!uploadResponse.ok) {
            throw new Error(`Upload failed: ${uploadResponse.statusText}`);
          }
          
          flierPath = response.data.uploadRecord.link;
        }
        
        // 3. Upload workdocuments if present
        let workDocsPaths = [];
        if (form.workdocuments && form.workdocuments.length > 0) {
          for (let i = 0; i < form.workdocuments.length; i++) {
            const file = form.workdocuments[i];
            if (file instanceof File) {
              // Generate a unique upload ID for this work document
              const uploadId = uuidv4();
              
              const ext = file.name.split('.').pop();
              const key = `public/hour-waivers/${hourwaiverId}/workDoc_${i}.${ext}`;
              
              // Get presigned URL for direct S3 upload
              const response = await axios.post('/api/upload-url', {
                filename: file.name,
                contentType: file.type,
                fileSize: file.size,
                uploadId: uploadId,
                details: 'Hour waiver work document creation',
                key: key
              }, {
                headers: { Authorization: `Bearer ${token}` }
              });
              
              // Upload file directly to S3 using presigned URL
              const uploadResponse = await fetch(response.data.presignedUrl, {
                method: 'PUT',
                body: file,
                headers: {
                  'Content-Type': file.type
                }
              });
              
              if (!uploadResponse.ok) {
                throw new Error(`Upload failed: ${uploadResponse.statusText}`);
              }
              
              workDocsPaths.push(response.data.uploadRecord.link);
            }
          }
        }
        
        // 4. Prepare finalData with correct file paths
        finalData = {
          ...getPreviewData(),
          id: hourwaiverId,
          flier: flierPath,
          workdocuments: workDocsPaths,
        };
        
        await axios.post('/api/hourwaivers', finalData, {
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
        });
      }
      
      setSubmitSuccess(true);
      setFinalSubmittedData(finalData);
      setOverlayMessage(waiverId ? '✅ Updated successfully!' : '✅ Submitted successfully!');
      setOverlaySuccess(true);
      
      if (waiverId) {
        // For updates, refresh the waiver data instead of navigating
        setTimeout(async () => {
          try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`/api/hourwaivers/${waiverId}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const updatedWaiver = res.data.hourwaiver;
            
            if (updatedWaiver) {
              // Update the form with fresh data
              setForm({
                ...initialForm,
                ...updatedWaiver,
                date: new Date(updatedWaiver.date).toISOString().split('T')[0] || '',
                timeStart: updatedWaiver.timestart || '',
                timeEnd: updatedWaiver.timeend || '',
                concentrations: updatedWaiver.concentrations || [],
                workdocuments: updatedWaiver.workdocuments || [],
                flier: updatedWaiver.flier || null,
                supervisor: {
                  firstName: updatedWaiver.supervisor_firstname || '',
                  lastName: updatedWaiver.supervisor_lastname || '',
                  title: updatedWaiver.supervisor_title || '',
                  email: updatedWaiver.supervisor_email || '',
                  phone: updatedWaiver.supervisor_phone || '',
                  signature: updatedWaiver.supervisor_signature || ''
                },
                tor: updatedWaiver.tor || '',
                survey: updatedWaiver.surveydata || [],
                progress: updatedWaiver.progress || [],
                taxonomy: updatedWaiver.taxonomy || [],
              selectedConcentrations: updatedWaiver.selectedConcentrations || []
              });
              
              // Update original data for comparison
              setOriginalWaiverData(updatedWaiver);
              
              // Refresh student info if needed
              if (updatedWaiver.enrollment && studentView) {
                try {
                  const studentInfoRes = await axios.get(`/api/enrollments/${updatedWaiver.enrollment}/full-student-info`, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  setLoadedStudentInfo(studentInfoRes.data);
    } catch (err) {
                  console.error('Error refreshing student info:', err);
                }
              }
              
              // Update selectedConcentrations with migration logic
              setSelectedConcentrations(migrateConcentrations(updatedWaiver, concentrations));
            }
          } catch (err) {
            console.error('Error refreshing waiver data:', err);
          }
          setShowOverlay(false);
        }, 1500);
      } else {
        // For new submissions, navigate to MyHours
        setTimeout(() => {
          setShowOverlay(false);
          navigate('/my-hours');
        }, 1500);
      }
      
    } catch (err) {
      console.error('Submission error:', err);
      
      if (err.response?.status === 409) {
        // Version conflict - don't auto-dismiss
        setSubmitError('The hour waiver was updated while you were working. Please reload the page to see the latest version.');
        setOverlayMessage('Version conflict detected. Please reload the page.');
        setOverlaySuccess(false);
        // Don't set timeout for version conflicts - let user manually reload
      } else {
        setSubmitError(err.response?.data?.error || 'Submission failed. Please try again.');
        setOverlayMessage(err.response?.data?.error || 'Submission failed. Please try again.');
        setOverlaySuccess(false);
        setTimeout(() => setShowOverlay(false), 3000);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Generate preview data for hourwaivers table
  const getPreviewData = () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      return { error: 'Form validation failed', errors: errs };
    }


    // Create stringdatafortimeofemails
    const stringdatafortimeofemails = JSON.stringify({
      studentName: `${userData?.first_name || ''} ${userData?.last_name || ''}`.trim(),
      studentEmail: defaultAccessCard?.email || '',
      studentSchool_id: defaultAccessCard?.school?.school_id || ''
    });

    // Build surveydata as an array of question objects with responses
    let surveydata = [];
    if (assignmentViewerRef.current && typeof assignmentViewerRef.current.getSurveyData === 'function') {
      surveydata = assignmentViewerRef.current.getSurveyData();
    }

    // Determine status based on progress - if System Admin Approval is complete, status should be 'Verified'
    // Otherwise, preserve existing status if it's 'Verified', or default to 'Submitted'
    let status = 'Submitted';
    const currentProgress = form.progress || [];
    const systemAdminStep = currentProgress.find(step => 
      (step.decider === 'systemAdmin' || step.title === 'System Admin Approval') && 
      step.status === 'complete'
    );
    
    if (systemAdminStep) {
      status = 'Verified';
    } else if (form.status === 'Verified') {
      // Preserve Verified status if it exists (in case progress hasn't been updated yet)
      status = 'Verified';
    }

    const previewData = {
      type: form.type,
      status: status,
      enrollment: validEnrollment?.enrollment_id,
      hours: parseFloat(form.hours),
      date: form.date,
      timestart: form.timeStart,
      timeend: form.timeEnd,
      description: form.description,
      concentrations: form.concentrations,
      hasotherconcentration: form.hasOtherConcentration,
      otherconcentration: form.otherConcentration,
      updatehistory: [
        {
          action: "Request Created",
          timestamp: new Date().toISOString(),
          who: `${userData?.first_name || ''} ${userData?.last_name || ''} (${userData?.primary_email || ''})`.trim()
        }
      ],
      flier: form.flier ? `[File: ${form.flier.name}]` : null,
      workdocuments: form.workdocuments.map(f => `[File: ${f.name}]`),
      supervisor_firstname: form.supervisor.firstName,
      supervisor_lastname: form.supervisor.lastName,
      supervisor_title: form.supervisor.title,
      supervisor_email: form.supervisor.email,
      supervisor_phone: form.supervisor.phone,
      supervisor_code: form.supervisor.code,
      tor: form.tor,
      surveydata: surveydata,
      progress: form.progress || [
        {
          step: 1,
          title: "Student Submission",
          status: "complete"
        }
      ],
      stringdatafortimeofemails: stringdatafortimeofemails,
      revision: 0,
      taxonomy: form.taxonomy,
      selectedConcentrations: form.selectedConcentrations
    };
    
    
    return previewData;
  };

  // Check permissions for current user based on progress
  const checkUserPermissions = () => {
    if (!studentView) return { canModify: true, canDecide: false };
    
    const progress = form.progress || [];
    const requiredStepForUser = requiredSteps.find(step => step.decider === currentUserRole);
    
    // Special handling for TOR - treat them like supervisors
    if (currentUserRole === 'tor') {
      // TOR should have the same permissions as supervisors
      const supervisorStep = requiredSteps.find(step => step.decider === 'supervisor');
      if (supervisorStep) {
        const progressForUserStep = progress.find(p => p.step === supervisorStep.step);
        const decision = progressForUserStep ? progressForUserStep.status : null;

        // Find the highest completed step (either complete or rejected)
        let highestStep = 0;
        let highestStatus = null;
        progress.forEach(p => {
          if ((p.status === 'complete' || p.status === 'rejected') && p.step > highestStep) {
            highestStep = p.step;
            highestStatus = p.status;
          }
        });

        const isHighestRejected = highestStatus === 'rejected';
        let canModify = false;
        let canDecide = false;
        if (isHighestRejected) {
          // If highest is a denial, anyone may update
          canModify = true;
          canDecide = true;
        } else if (highestStatus === 'complete') {
          // Only users at or above the highest completed step can update/decide
          canModify = supervisorStep.step >= highestStep;
          canDecide = supervisorStep.step >= highestStep;
        } else {
          // No approvals/denials yet, all can act
          canModify = true;
          canDecide = true;
        }

        return { canModify, canDecide, decision, isHighestRejected };
      }
    }
    
    if (!requiredStepForUser) return { canModify: false, canDecide: false };

    const progressForUserStep = progress.find(p => p.step === requiredStepForUser.step);
    const decision = progressForUserStep ? progressForUserStep.status : null;

    // Find the highest completed step (either complete or rejected)
    let highestStep = 0;
    let highestStatus = null;
    progress.forEach(p => {
      if ((p.status === 'complete' || p.status === 'rejected') && p.step > highestStep) {
        highestStep = p.step;
        highestStatus = p.status;
      }
    });

    const isHighestRejected = highestStatus === 'rejected';
    let canModify = false;
    let canDecide = false;
    if (isHighestRejected) {
      // If highest is a denial, anyone may update
      canModify = true;
      canDecide = true;
    } else if (highestStatus === 'complete') {
      // Only users at or above the highest completed step can update/decide
      canModify = requiredStepForUser.step >= highestStep;
      canDecide = requiredStepForUser.step >= highestStep;
    } else {
      // No approvals/denials yet, all can act
      canModify = true;
      canDecide = true;
    }

    return { canModify, canDecide, decision, isHighestRejected };
  };

  const userPermissions = checkUserPermissions();
  // For now, let's make the form editable for all users in student view to see the files and survey
  const isFormEditable = !studentView || userPermissions.canModify;
  
  console.log('Form state:', {
    studentView,
    isFormEditable,
    flier: form.flier,
    workdocuments: form.workdocuments,
    survey: form.survey,
    progress: form.progress
  });

  // Approval action handlers
  const handleApprove = async () => {
    if (!form.supervisor.signature) {
      setSignatureError('Signature required to approve.');
      return;
    }
    
    // Debug: log signature data
    console.log('Signature data:', form.supervisor.signature);
    console.log('Signature length:', form.supervisor.signature.length);
    console.log('Signature starts with data:', form.supervisor.signature.startsWith('data:image/png;base64,'));
    
    setSignatureError('');
    setSubmitting(true);
    setShowOverlay(true);
    setOverlayMessage('Approving and saving changes...');
    setOverlaySuccess(false);
    
    try {
      const token = localStorage.getItem('token');
      
      // Handle file uploads for updates
      let flierPath = null;
      let workDocsPaths = [];
      
      // Only upload new files if they're actual File objects (not existing URLs)
      if (form.flier && form.flier instanceof File) {
        const ext = form.flier.name.split('.').pop();
        const key = `public/hour-waivers/${waiverId}/flier.${ext}`;
        const formData = new FormData();
        formData.append('file', form.flier);
        formData.append('key', key);
        const response = await axios.post('/api/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` },
        });
        flierPath = response.data.url || key;
      } else {
        // Keep existing flier path
        flierPath = form.flier;
      }
      
      if (form.workdocuments && form.workdocuments.length > 0) {
        for (let i = 0; i < form.workdocuments.length; i++) {
          const file = form.workdocuments[i];
          if (file instanceof File) {
            // Upload new file
            const ext = file.name.split('.').pop();
            const key = `public/hour-waivers/${waiverId}/workDoc_${i}.${ext}`;
            const formData = new FormData();
            formData.append('file', file);
            formData.append('key', key);
            const response = await axios.post('/api/upload', formData, {
              headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` },
            });
            workDocsPaths.push(response.data.url || key);
          } else {
            // Keep existing file path
            workDocsPaths.push(file);
          }
        }
      }
      
      // Prepare update data
      const updateData = getPreviewData();
      updateData.flier = flierPath;
      updateData.workdocuments = workDocsPaths;
      updateData.revision = form.revision || 0;
      updateData.expectedUpdatedDate = form.updateddate;
      updateData.supervisor_signature = form.supervisor.signature;
      
      // Update progress to mark supervisor approval as complete
      const currentProgress = form.progress || [];
      const updatedProgress = currentProgress.map(step => {
        if (step.decider === 'supervisor' || step.title === 'Supervisor Approval') {
          return {
            ...step,
            status: 'complete',
            who: `${userData?.first_name || ''} ${userData?.last_name || ''} (${userData?.primary_email || ''})`.trim(),
            completedAt: new Date().toISOString()
          };
        }
        return step;
      });
      
      // If supervisor step doesn't exist, add it
      if (!updatedProgress.find(step => step.decider === 'supervisor' || step.title === 'Supervisor Approval')) {
        updatedProgress.push({
          step: 2,
          decider: 'supervisor',
          title: 'Supervisor Approval',
          status: 'complete',
          who: `${userData?.first_name || ''} ${userData?.last_name || ''} (${userData?.primary_email || ''})`.trim(),
          completedAt: new Date().toISOString()
        });
      }
      
      updateData.progress = updatedProgress;
      
      // Check if this is a decision change
      const existingSupervisorAction = (form.updatehistory || []).find(entry => 
        entry.action === "approved by supervisor" || entry.action === "denied by supervisor"
      );
      
      // Add approval entry to update history
      const modificationsApprove = computeModifications(updateData, originalWaiverData, form, flierPath, workDocsPaths);
      const approvalEntry = {
        action: existingSupervisorAction ? "changed decision to approved by supervisor" : "approved by supervisor",
        timestamp: new Date().toISOString(),
        who: `${userData?.first_name || ''} ${userData?.last_name || ''} (${userData?.primary_email || ''})`.trim(),
        modifications: modificationsApprove
      };
      updateData.updatehistory = [
        approvalEntry,
        ...(form.updatehistory || [])
      ];
      
      // Debug: log the update data being sent
      console.log('Update data being sent:', updateData);
      console.log('Supervisor signature in update data:', updateData.supervisor_signature);
      console.log('Supervisor signature length:', updateData.supervisor_signature?.length);
      
      // Send PATCH request
      await axios.patch(`/api/hourwaivers/${waiverId}`, updateData, {
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
      
      setSubmitSuccess(true);
      setOverlayMessage('✅ Approved successfully!');
      setOverlaySuccess(true);
      
      // Hide signature pad after successful approval so supervisor must sign again for next decision
      setSignaturePadHidden(true);
      setForm(f => ({ ...f, supervisor: { ...f.supervisor, signature: '' } }));
      if (signaturePadRef.current) {
        signaturePadRef.current.clear();
      }
      
      // Refresh the waiver data to show updated progress
      setTimeout(async () => {
        try {
          const res = await axios.get(`/api/hourwaivers/${waiverId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const updatedWaiver = res.data.hourwaiver;
          
          if (updatedWaiver) {
            // Update the form with fresh data
            setForm({
              ...initialForm,
              ...updatedWaiver,
              status: updatedWaiver.status || 'Submitted', // Explicitly preserve status
              date: new Date(updatedWaiver.date).toISOString().split('T')[0] || '',
              timeStart: updatedWaiver.timestart || '',
              timeEnd: updatedWaiver.timeend || '',
              concentrations: updatedWaiver.concentrations || [],
              workdocuments: updatedWaiver.workdocuments || [],
              flier: updatedWaiver.flier || null,
              supervisor: {
                firstName: updatedWaiver.supervisor_firstname || '',
                lastName: updatedWaiver.supervisor_lastname || '',
                title: updatedWaiver.supervisor_title || '',
                email: updatedWaiver.supervisor_email || '',
                phone: updatedWaiver.supervisor_phone || '',
                signature: updatedWaiver.supervisor_signature || ''
              },
              tor: updatedWaiver.tor || '',
              survey: updatedWaiver.surveydata || [],
              progress: updatedWaiver.progress || [],
              taxonomy: updatedWaiver.taxonomy || [],
              selectedConcentrations: updatedWaiver.selectedConcentrations || []
            });
            
            // Update original data for comparison
            setOriginalWaiverData(updatedWaiver);
            
            // Update selectedConcentrations with migration logic
            const migratedConcentrations = migrateConcentrations(updatedWaiver, concentrations);
            setSelectedConcentrations(migratedConcentrations);
            
            // Also ensure form.concentrations has the concentration IDs
            if (updatedWaiver.concentrations && Array.isArray(updatedWaiver.concentrations)) {
              setForm(f => ({ ...f, concentrations: updatedWaiver.concentrations }));
            } else if (migratedConcentrations.length > 0) {
              setForm(f => ({ ...f, concentrations: migratedConcentrations.map(c => c.id) }));
            }
          }
        } catch (err) {
          console.error('Error refreshing waiver data after approval:', err);
        }
        setShowOverlay(false);
      }, 1500);
      
    } catch (err) {
      console.error('Approval error:', err);
      
      if (err.response?.status === 409) {
        setSubmitError('The hour waiver was updated while you were working. Please reload the page to see the latest version.');
        setOverlayMessage('Version conflict detected. Please reload the page.');
        setOverlaySuccess(false);
      } else {
        setSubmitError(err.response?.data?.error || 'Approval failed. Please try again.');
        setOverlayMessage(err.response?.data?.error || 'Approval failed. Please try again.');
        setOverlaySuccess(false);
        setTimeout(() => setShowOverlay(false), 3000);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSchoolAdminApprove = async () => {
    setSubmitting(true);
    setShowOverlay(true);
    setOverlayMessage('Approving and saving changes...');
    setOverlaySuccess(false);
    
    try {
      const token = localStorage.getItem('token');
      
      // Handle file uploads for updates
      let flierPath = null;
      let workDocsPaths = [];
      
      // Only upload new files if they're actual File objects (not existing URLs)
      if (form.flier && form.flier instanceof File) {
        const ext = form.flier.name.split('.').pop();
        const key = `public/hour-waivers/${waiverId}/flier.${ext}`;
        const formData = new FormData();
        formData.append('file', form.flier);
        formData.append('key', key);
        const response = await axios.post('/api/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` },
        });
        flierPath = response.data.url || key;
      } else {
        // Keep existing flier path
        flierPath = form.flier;
      }
      
      if (form.workdocuments && form.workdocuments.length > 0) {
        for (let i = 0; i < form.workdocuments.length; i++) {
          const file = form.workdocuments[i];
          if (file instanceof File) {
            // Upload new file
            const ext = file.name.split('.').pop();
            const key = `public/hour-waivers/${waiverId}/workDoc_${i}.${ext}`;
            const formData = new FormData();
            formData.append('file', file);
            formData.append('key', key);
            const response = await axios.post('/api/upload', formData, {
              headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` },
            });
            workDocsPaths.push(response.data.url || key);
          } else {
            // Keep existing file path
            workDocsPaths.push(file);
          }
        }
      }
      
      // Prepare update data
      const updateData = getPreviewData();
      updateData.flier = flierPath;
      updateData.workdocuments = workDocsPaths;
      updateData.revision = form.revision || 0;
      updateData.expectedUpdatedDate = form.updateddate;
      
      // Update progress to mark school admin approval as complete
      const currentProgress = form.progress || [];
      const updatedProgress = currentProgress.map(step => {
        if (step.decider === 'schoolAdmin' || step.title === 'School Admin Approval') {
          return {
            ...step,
            status: 'complete',
            who: `${userData?.first_name || ''} ${userData?.last_name || ''} (${userData?.primary_email || ''})`.trim(),
            completedAt: new Date().toISOString()
          };
        }
        return step;
      });
      
      // If school admin step doesn't exist, add it
      if (!updatedProgress.find(step => step.decider === 'schoolAdmin' || step.title === 'School Admin Approval')) {
        updatedProgress.push({
          step: 3,
          decider: 'schoolAdmin',
          title: 'School Admin Approval',
          status: 'complete',
          who: `${userData?.first_name || ''} ${userData?.last_name || ''} (${userData?.primary_email || ''})`.trim(),
          completedAt: new Date().toISOString()
        });
      }
      
      updateData.progress = updatedProgress;
      
      // Check if this is a decision change
      const existingSchoolAdminAction = (form.updatehistory || []).find(entry => 
        entry.action === "approved by school admin" || entry.action === "denied by school admin"
      );
      
      // Add approval entry to update history
      const modificationsSchoolAdminApprove = computeModifications(updateData, originalWaiverData, form, flierPath, workDocsPaths);
      const approvalEntrySchoolAdmin = {
        action: existingSchoolAdminAction ? "changed decision to approved by school admin" : "approved by school admin",
        timestamp: new Date().toISOString(),
        who: `${userData?.first_name || ''} ${userData?.last_name || ''} (${userData?.primary_email || ''})`.trim(),
        modifications: modificationsSchoolAdminApprove
      };
      updateData.updatehistory = [
        approvalEntrySchoolAdmin,
        ...(form.updatehistory || [])
      ];
      
      // Send PATCH request
      await axios.patch(`/api/hourwaivers/${waiverId}`, updateData, {
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
      
      setSubmitSuccess(true);
      setOverlayMessage('✅ Approved successfully!');
      setOverlaySuccess(true);
      
      // Refresh the waiver data to show updated progress
      setTimeout(async () => {
        try {
          const res = await axios.get(`/api/hourwaivers/${waiverId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const updatedWaiver = res.data.hourwaiver;
          
          if (updatedWaiver) {
            // Update the form with fresh data
            setForm({
              ...initialForm,
              ...updatedWaiver,
              status: updatedWaiver.status || 'Submitted', // Explicitly preserve status
              date: new Date(updatedWaiver.date).toISOString().split('T')[0] || '',
              timeStart: updatedWaiver.timestart || '',
              timeEnd: updatedWaiver.timeend || '',
              concentrations: updatedWaiver.concentrations || [],
              workdocuments: updatedWaiver.workdocuments || [],
              flier: updatedWaiver.flier || null,
              supervisor: {
                firstName: updatedWaiver.supervisor_firstname || '',
                lastName: updatedWaiver.supervisor_lastname || '',
                title: updatedWaiver.supervisor_title || '',
                email: updatedWaiver.supervisor_email || '',
                phone: updatedWaiver.supervisor_phone || '',
                signature: updatedWaiver.supervisor_signature || ''
              },
              tor: updatedWaiver.tor || '',
              survey: updatedWaiver.surveydata || [],
              progress: updatedWaiver.progress || [],
              taxonomy: updatedWaiver.taxonomy || [],
              selectedConcentrations: updatedWaiver.selectedConcentrations || []
            });
            
            // Update original data for comparison
            setOriginalWaiverData(updatedWaiver);
            
            // Update selectedConcentrations with migration logic
            const migratedConcentrations = migrateConcentrations(updatedWaiver, concentrations);
            setSelectedConcentrations(migratedConcentrations);
            
            // Also ensure form.concentrations has the concentration IDs
            if (updatedWaiver.concentrations && Array.isArray(updatedWaiver.concentrations)) {
              setForm(f => ({ ...f, concentrations: updatedWaiver.concentrations }));
            } else if (migratedConcentrations.length > 0) {
              setForm(f => ({ ...f, concentrations: migratedConcentrations.map(c => c.id) }));
            }
          }
        } catch (err) {
          console.error('Error refreshing waiver data after approval:', err);
        }
        setShowOverlay(false);
      }, 1500);
      
    } catch (err) {
      console.error('Approval error:', err);
      
      if (err.response?.status === 409) {
        setSubmitError('The hour waiver was updated while you were working. Please reload the page to see the latest version.');
        setOverlayMessage('Version conflict detected. Please reload the page.');
        setOverlaySuccess(false);
      } else {
        setSubmitError(err.response?.data?.error || 'Approval failed. Please try again.');
        setOverlayMessage(err.response?.data?.error || 'Approval failed. Please try again.');
        setOverlaySuccess(false);
        setTimeout(() => setShowOverlay(false), 3000);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSchoolAdminDeny = async () => {
    setSubmitting(true);
    setShowOverlay(true);
    setOverlayMessage('Denying and saving changes...');
    setOverlaySuccess(false);
    
    try {
      const token = localStorage.getItem('token');
      
      // Handle file uploads for updates
      let flierPath = null;
      let workDocsPaths = [];
      
      // Only upload new files if they're actual File objects (not existing URLs)
      if (form.flier && form.flier instanceof File) {
        const ext = form.flier.name.split('.').pop();
        const key = `public/hour-waivers/${waiverId}/flier.${ext}`;
        const formData = new FormData();
        formData.append('file', form.flier);
        formData.append('key', key);
        const response = await axios.post('/api/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` },
        });
        flierPath = response.data.url || key;
      } else {
        // Keep existing flier path
        flierPath = form.flier;
      }
      
      if (form.workdocuments && form.workdocuments.length > 0) {
        for (let i = 0; i < form.workdocuments.length; i++) {
          const file = form.workdocuments[i];
          if (file instanceof File) {
            // Upload new file
            const ext = file.name.split('.').pop();
            const key = `public/hour-waivers/${waiverId}/workDoc_${i}.${ext}`;
            const formData = new FormData();
            formData.append('file', file);
            formData.append('key', key);
            const response = await axios.post('/api/upload', formData, {
              headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` },
            });
            workDocsPaths.push(response.data.url || key);
          } else {
            // Keep existing file path
            workDocsPaths.push(file);
          }
        }
      }
      
      // Prepare update data
      const updateData = getPreviewData();
      updateData.flier = flierPath;
      updateData.workdocuments = workDocsPaths;
      updateData.revision = form.revision || 0;
      updateData.expectedUpdatedDate = form.updateddate;
      
      // Update progress to mark school admin approval as rejected
      const currentProgress = form.progress || [];
      const updatedProgress = currentProgress.map(step => {
        if (step.decider === 'schoolAdmin' || step.title === 'School Admin Approval') {
          return {
            ...step,
            status: 'rejected',
            who: `${userData?.first_name || ''} ${userData?.last_name || ''} (${userData?.primary_email || ''})`.trim(),
            completedAt: new Date().toISOString()
          };
        }
        return step;
      });
      
      // If school admin step doesn't exist, add it
      if (!updatedProgress.find(step => step.decider === 'schoolAdmin' || step.title === 'School Admin Approval')) {
        updatedProgress.push({
          step: 3,
          decider: 'schoolAdmin',
          title: 'School Admin Approval',
          status: 'rejected',
          who: `${userData?.first_name || ''} ${userData?.last_name || ''} (${userData?.primary_email || ''})`.trim(),
          completedAt: new Date().toISOString()
        });
      }
      
      updateData.progress = updatedProgress;
      
      // Check if this is a decision change
      const existingSchoolAdminAction = (form.updatehistory || []).find(entry => 
        entry.action === "approved by school admin" || entry.action === "denied by school admin"
      );
      
      // Add denial entry to update history
      const modificationsSchoolAdminDeny = computeModifications(updateData, originalWaiverData, form, flierPath, workDocsPaths);
      const denialEntrySchoolAdmin = {
        action: existingSchoolAdminAction ? "changed decision to denied by school admin" : "denied by school admin",
        timestamp: new Date().toISOString(),
        who: `${userData?.first_name || ''} ${userData?.last_name || ''} (${userData?.primary_email || ''})`.trim(),
        modifications: modificationsSchoolAdminDeny
      };
      updateData.updatehistory = [
        denialEntrySchoolAdmin,
        ...(form.updatehistory || [])
      ];
      
      // Send PATCH request
      await axios.patch(`/api/hourwaivers/${waiverId}`, updateData, {
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
      
      setSubmitSuccess(true);
      setOverlayMessage('✅ Denied successfully!');
      setOverlaySuccess(true);
      
      // Refresh the waiver data to show updated progress
      setTimeout(async () => {
        try {
          const res = await axios.get(`/api/hourwaivers/${waiverId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const updatedWaiver = res.data.hourwaiver;
          
          if (updatedWaiver) {
            // Update the form with fresh data
            setForm({
              ...initialForm,
              ...updatedWaiver,
              status: updatedWaiver.status || 'Submitted', // Explicitly preserve status
              date: new Date(updatedWaiver.date).toISOString().split('T')[0] || '',
              timeStart: updatedWaiver.timestart || '',
              timeEnd: updatedWaiver.timeend || '',
              concentrations: updatedWaiver.concentrations || [],
              workdocuments: updatedWaiver.workdocuments || [],
              flier: updatedWaiver.flier || null,
              supervisor: {
                firstName: updatedWaiver.supervisor_firstname || '',
                lastName: updatedWaiver.supervisor_lastname || '',
                title: updatedWaiver.supervisor_title || '',
                email: updatedWaiver.supervisor_email || '',
                phone: updatedWaiver.supervisor_phone || '',
                signature: updatedWaiver.supervisor_signature || ''
              },
              tor: updatedWaiver.tor || '',
              survey: updatedWaiver.surveydata || [],
              progress: updatedWaiver.progress || [],
              taxonomy: updatedWaiver.taxonomy || [],
              selectedConcentrations: updatedWaiver.selectedConcentrations || []
            });
            
            // Update original data for comparison
            setOriginalWaiverData(updatedWaiver);
            
            // Update selectedConcentrations with migration logic
            const migratedConcentrations = migrateConcentrations(updatedWaiver, concentrations);
            setSelectedConcentrations(migratedConcentrations);
            
            // Also ensure form.concentrations has the concentration IDs
            if (updatedWaiver.concentrations && Array.isArray(updatedWaiver.concentrations)) {
              setForm(f => ({ ...f, concentrations: updatedWaiver.concentrations }));
            } else if (migratedConcentrations.length > 0) {
              setForm(f => ({ ...f, concentrations: migratedConcentrations.map(c => c.id) }));
            }
          }
        } catch (err) {
          console.error('Error refreshing waiver data after denial:', err);
        }
        setShowOverlay(false);
      }, 1500);
      
    } catch (err) {
      console.error('Denial error:', err);
      
      if (err.response?.status === 409) {
        setSubmitError('The hour waiver was updated while you were working. Please reload the page to see the latest version.');
        setOverlayMessage('Version conflict detected. Please reload the page.');
        setOverlaySuccess(false);
      } else {
        setSubmitError(err.response?.data?.error || 'Denial failed. Please try again.');
        setOverlayMessage(err.response?.data?.error || 'Denial failed. Please try again.');
        setOverlaySuccess(false);
        setTimeout(() => setShowOverlay(false), 3000);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSystemAdminApprove = async () => {
    setSubmitting(true);
    setShowOverlay(true);
    setOverlayMessage('Approving and saving changes...');
    setOverlaySuccess(false);
    
    try {
      const token = localStorage.getItem('token');
      
      // Handle file uploads for updates
      let flierPath = null;
      let workDocsPaths = [];
      
      // Only upload new files if they're actual File objects (not existing URLs)
      if (form.flier && form.flier instanceof File) {
        const ext = form.flier.name.split('.').pop();
        const key = `public/hour-waivers/${waiverId}/flier.${ext}`;
        const formData = new FormData();
        formData.append('file', form.flier);
        formData.append('key', key);
        const response = await axios.post('/api/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` },
        });
        flierPath = response.data.url || key;
      } else {
        // Keep existing flier path
        flierPath = form.flier;
      }
      
      if (form.workdocuments && form.workdocuments.length > 0) {
        for (let i = 0; i < form.workdocuments.length; i++) {
          const file = form.workdocuments[i];
          if (file instanceof File) {
            // Upload new file
            const ext = file.name.split('.').pop();
            const key = `public/hour-waivers/${waiverId}/workDoc_${i}.${ext}`;
            const formData = new FormData();
            formData.append('file', file);
            formData.append('key', key);
            const response = await axios.post('/api/upload', formData, {
              headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` },
            });
            workDocsPaths.push(response.data.url || key);
          } else {
            // Keep existing file path
            workDocsPaths.push(file);
          }
        }
      }
      
      // Prepare update data
      const updateData = getPreviewData();
      updateData.flier = flierPath;
      updateData.workdocuments = workDocsPaths;
      updateData.revision = form.revision || 0;
      updateData.expectedUpdatedDate = form.updateddate;
      updateData.status = 'Verified'; // Set status to Verified when system admin approves
      
      // Update progress to mark system admin approval as complete
      const currentProgress = form.progress || [];
      const updatedProgress = currentProgress.map(step => {
        if (step.decider === 'systemAdmin' || step.title === 'System Admin Approval') {
          return {
            ...step,
            status: 'complete',
            who: `${userData?.first_name || ''} ${userData?.last_name || ''} (${userData?.primary_email || ''})`.trim(),
            completedAt: new Date().toISOString()
          };
        }
        return step;
      });
      
      // If system admin step doesn't exist, add it
      if (!updatedProgress.find(step => step.decider === 'systemAdmin' || step.title === 'System Admin Approval')) {
        updatedProgress.push({
          step: 4,
          decider: 'systemAdmin',
          title: 'System Admin Approval',
          status: 'complete',
          who: `${userData?.first_name || ''} ${userData?.last_name || ''} (${userData?.primary_email || ''})`.trim(),
          completedAt: new Date().toISOString()
        });
      }
      
      updateData.progress = updatedProgress;
      
      // Check if this is a decision change
      const existingSystemAdminAction = (form.updatehistory || []).find(entry => 
        entry.action === "approved by system admin" || entry.action === "denied by system admin"
      );
      
      // Add approval entry to update history
      const modificationsSystemAdminApprove = computeModifications(updateData, originalWaiverData, form, flierPath, workDocsPaths);
      const approvalEntrySystemAdmin = {
        action: existingSystemAdminAction ? "changed decision to approved by system admin" : "approved by system admin",
        timestamp: new Date().toISOString(),
        who: `${userData?.first_name || ''} ${userData?.last_name || ''} (${userData?.primary_email || ''})`.trim(),
        modifications: modificationsSystemAdminApprove
      };
      updateData.updatehistory = [
        approvalEntrySystemAdmin,
        ...(form.updatehistory || [])
      ];
      
      // Ensure status is set to Verified (getPreviewData should have set it, but double-check)
      updateData.status = 'Verified';
      
      console.log('System Admin Approval - Sending update with status:', updateData.status);
      console.log('System Admin Approval - Progress:', updateData.progress);
      
      // Send PATCH request
      await axios.patch(`/api/hourwaivers/${waiverId}`, updateData, {
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
      
      setSubmitSuccess(true);
      setOverlayMessage('✅ Approved successfully!');
      setOverlaySuccess(true);
      
      // Refresh the waiver data to show updated progress
      setTimeout(async () => {
        try {
          const res = await axios.get(`/api/hourwaivers/${waiverId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const updatedWaiver = res.data.hourwaiver;
          
          if (updatedWaiver) {
            // Update the form with fresh data
            setForm({
              ...initialForm,
              ...updatedWaiver,
              status: updatedWaiver.status || 'Submitted', // Explicitly preserve status
              date: new Date(updatedWaiver.date).toISOString().split('T')[0] || '',
              timeStart: updatedWaiver.timestart || '',
              timeEnd: updatedWaiver.timeend || '',
              concentrations: updatedWaiver.concentrations || [],
              workdocuments: updatedWaiver.workdocuments || [],
              flier: updatedWaiver.flier || null,
              supervisor: {
                firstName: updatedWaiver.supervisor_firstname || '',
                lastName: updatedWaiver.supervisor_lastname || '',
                title: updatedWaiver.supervisor_title || '',
                email: updatedWaiver.supervisor_email || '',
                phone: updatedWaiver.supervisor_phone || '',
                signature: updatedWaiver.supervisor_signature || ''
              },
              tor: updatedWaiver.tor || '',
              survey: updatedWaiver.surveydata || [],
              progress: updatedWaiver.progress || [],
              taxonomy: updatedWaiver.taxonomy || [],
              selectedConcentrations: updatedWaiver.selectedConcentrations || []
            });
            
            // Update original data for comparison
            setOriginalWaiverData(updatedWaiver);
            
            // Update selectedConcentrations with migration logic
            const migratedConcentrations = migrateConcentrations(updatedWaiver, concentrations);
            setSelectedConcentrations(migratedConcentrations);
            
            // Also ensure form.concentrations has the concentration IDs
            if (updatedWaiver.concentrations && Array.isArray(updatedWaiver.concentrations)) {
              setForm(f => ({ ...f, concentrations: updatedWaiver.concentrations }));
            } else if (migratedConcentrations.length > 0) {
              setForm(f => ({ ...f, concentrations: migratedConcentrations.map(c => c.id) }));
            }
          }
        } catch (err) {
          console.error('Error refreshing waiver data after approval:', err);
        }
        setShowOverlay(false);
      }, 1500);
      
    } catch (err) {
      console.error('Approval error:', err);
      
      if (err.response?.status === 409) {
        setSubmitError('The hour waiver was updated while you were working. Please reload the page to see the latest version.');
        setOverlayMessage('Version conflict detected. Please reload the page.');
        setOverlaySuccess(false);
      } else {
        setSubmitError(err.response?.data?.error || 'Approval failed. Please try again.');
        setOverlayMessage(err.response?.data?.error || 'Approval failed. Please try again.');
        setOverlaySuccess(false);
        setTimeout(() => setShowOverlay(false), 3000);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSystemAdminDeny = async () => {
    setSubmitting(true);
    setShowOverlay(true);
    setOverlayMessage('Denying and saving changes...');
    setOverlaySuccess(false);
    
    try {
      const token = localStorage.getItem('token');
      
      // Handle file uploads for updates
      let flierPath = null;
      let workDocsPaths = [];
      
      // Only upload new files if they're actual File objects (not existing URLs)
      if (form.flier && form.flier instanceof File) {
        const ext = form.flier.name.split('.').pop();
        const key = `public/hour-waivers/${waiverId}/flier.${ext}`;
        const formData = new FormData();
        formData.append('file', form.flier);
        formData.append('key', key);
        const response = await axios.post('/api/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` },
        });
        flierPath = response.data.url || key;
      } else {
        // Keep existing flier path
        flierPath = form.flier;
      }
      
      if (form.workdocuments && form.workdocuments.length > 0) {
        for (let i = 0; i < form.workdocuments.length; i++) {
          const file = form.workdocuments[i];
          if (file instanceof File) {
            // Upload new file
            const ext = file.name.split('.').pop();
            const key = `public/hour-waivers/${waiverId}/workDoc_${i}.${ext}`;
            const formData = new FormData();
            formData.append('file', file);
            formData.append('key', key);
            const response = await axios.post('/api/upload', formData, {
              headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` },
            });
            workDocsPaths.push(response.data.url || key);
          } else {
            // Keep existing file path
            workDocsPaths.push(file);
          }
        }
      }
      
      // Prepare update data
      const updateData = getPreviewData();
      updateData.flier = flierPath;
      updateData.workdocuments = workDocsPaths;
      updateData.revision = form.revision || 0;
      updateData.expectedUpdatedDate = form.updateddate;
      
      // Update progress to mark system admin approval as rejected
      const currentProgress = form.progress || [];
      const updatedProgress = currentProgress.map(step => {
        if (step.decider === 'systemAdmin' || step.title === 'System Admin Approval') {
          return {
            ...step,
            status: 'rejected',
            who: `${userData?.first_name || ''} ${userData?.last_name || ''} (${userData?.primary_email || ''})`.trim(),
            completedAt: new Date().toISOString()
          };
        }
        return step;
      });
      
      // If system admin step doesn't exist, add it
      if (!updatedProgress.find(step => step.decider === 'systemAdmin' || step.title === 'System Admin Approval')) {
        updatedProgress.push({
          step: 4,
          decider: 'systemAdmin',
          title: 'System Admin Approval',
          status: 'rejected',
          who: `${userData?.first_name || ''} ${userData?.last_name || ''} (${userData?.primary_email || ''})`.trim(),
          completedAt: new Date().toISOString()
        });
      }
      
      updateData.progress = updatedProgress;
      
      // Check if this is a decision change
      const existingSystemAdminAction = (form.updatehistory || []).find(entry => 
        entry.action === "approved by system admin" || entry.action === "denied by system admin"
      );
      
      // Add denial entry to update history
      const modificationsSystemAdminDeny = computeModifications(updateData, originalWaiverData, form, flierPath, workDocsPaths);
      const denialEntrySystemAdmin = {
        action: existingSystemAdminAction ? "changed decision to denied by system admin" : "denied by system admin",
        timestamp: new Date().toISOString(),
        who: `${userData?.first_name || ''} ${userData?.last_name || ''} (${userData?.primary_email || ''})`.trim(),
        modifications: modificationsSystemAdminDeny
      };
      updateData.updatehistory = [
        denialEntrySystemAdmin,
        ...(form.updatehistory || [])
      ];
      
      // Send PATCH request
      await axios.patch(`/api/hourwaivers/${waiverId}`, updateData, {
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
      
      setSubmitSuccess(true);
      setOverlayMessage('✅ Denied successfully!');
      setOverlaySuccess(true);
      
      // Refresh the waiver data to show updated progress
      setTimeout(async () => {
        try {
          const res = await axios.get(`/api/hourwaivers/${waiverId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const updatedWaiver = res.data.hourwaiver;
          
          if (updatedWaiver) {
            // Update the form with fresh data
            setForm({
              ...initialForm,
              ...updatedWaiver,
              status: updatedWaiver.status || 'Submitted', // Explicitly preserve status
              date: new Date(updatedWaiver.date).toISOString().split('T')[0] || '',
              timeStart: updatedWaiver.timestart || '',
              timeEnd: updatedWaiver.timeend || '',
              concentrations: updatedWaiver.concentrations || [],
              workdocuments: updatedWaiver.workdocuments || [],
              flier: updatedWaiver.flier || null,
              supervisor: {
                firstName: updatedWaiver.supervisor_firstname || '',
                lastName: updatedWaiver.supervisor_lastname || '',
                title: updatedWaiver.supervisor_title || '',
                email: updatedWaiver.supervisor_email || '',
                phone: updatedWaiver.supervisor_phone || '',
                signature: updatedWaiver.supervisor_signature || ''
              },
              tor: updatedWaiver.tor || '',
              survey: updatedWaiver.surveydata || [],
              progress: updatedWaiver.progress || [],
              taxonomy: updatedWaiver.taxonomy || [],
              selectedConcentrations: updatedWaiver.selectedConcentrations || []
            });
            
            // Update original data for comparison
            setOriginalWaiverData(updatedWaiver);
            
            // Update selectedConcentrations with migration logic
            const migratedConcentrations = migrateConcentrations(updatedWaiver, concentrations);
            setSelectedConcentrations(migratedConcentrations);
            
            // Also ensure form.concentrations has the concentration IDs
            if (updatedWaiver.concentrations && Array.isArray(updatedWaiver.concentrations)) {
              setForm(f => ({ ...f, concentrations: updatedWaiver.concentrations }));
            } else if (migratedConcentrations.length > 0) {
              setForm(f => ({ ...f, concentrations: migratedConcentrations.map(c => c.id) }));
            }
          }
        } catch (err) {
          console.error('Error refreshing waiver data after denial:', err);
        }
        setShowOverlay(false);
      }, 1500);
      
    } catch (err) {
      console.error('Denial error:', err);
      
      if (err.response?.status === 409) {
        setSubmitError('The hour waiver was updated while you were working. Please reload the page to see the latest version.');
        setOverlayMessage('Version conflict detected. Please reload the page.');
        setOverlaySuccess(false);
      } else {
        setSubmitError(err.response?.data?.error || 'Denial failed. Please try again.');
        setOverlayMessage(err.response?.data?.error || 'Denial failed. Please try again.');
        setOverlaySuccess(false);
        setTimeout(() => setShowOverlay(false), 3000);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeny = async () => {
    if (!form.supervisor.signature) {
      setSignatureError('Signature required to deny.');
      return;
    }
    
    // Debug: log signature data
    console.log('Signature data:', form.supervisor.signature);
    console.log('Signature length:', form.supervisor.signature.length);
    console.log('Signature starts with data:', form.supervisor.signature.startsWith('data:image/png;base64,'));
    
    setSignatureError('');
    setSubmitting(true);
    setShowOverlay(true);
    setOverlayMessage('Denying and saving changes...');
    setOverlaySuccess(false);
    
    try {
      const token = localStorage.getItem('token');
      
      // Handle file uploads for updates
      let flierPath = null;
      let workDocsPaths = [];
      
      // Only upload new files if they're actual File objects (not existing URLs)
      if (form.flier && form.flier instanceof File) {
        const ext = form.flier.name.split('.').pop();
        const key = `public/hour-waivers/${waiverId}/flier.${ext}`;
        const formData = new FormData();
        formData.append('file', form.flier);
        formData.append('key', key);
        const response = await axios.post('/api/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` },
        });
        flierPath = response.data.url || key;
      } else {
        // Keep existing flier path
        flierPath = form.flier;
      }
      
      if (form.workdocuments && form.workdocuments.length > 0) {
        for (let i = 0; i < form.workdocuments.length; i++) {
          const file = form.workdocuments[i];
          if (file instanceof File) {
            // Upload new file
            const ext = file.name.split('.').pop();
            const key = `public/hour-waivers/${waiverId}/workDoc_${i}.${ext}`;
            const formData = new FormData();
            formData.append('file', file);
            formData.append('key', key);
            const response = await axios.post('/api/upload', formData, {
              headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` },
            });
            workDocsPaths.push(response.data.url || key);
          } else {
            // Keep existing file path
            workDocsPaths.push(file);
          }
        }
      }
      
      // Prepare update data
      const updateData = getPreviewData();
      updateData.flier = flierPath;
      updateData.workdocuments = workDocsPaths;
      updateData.revision = form.revision || 0;
      updateData.expectedUpdatedDate = form.updateddate;
      updateData.supervisor_signature = form.supervisor.signature;
      
      // Update progress to mark supervisor approval as rejected
      const currentProgress = form.progress || [];
      const updatedProgress = currentProgress.map(step => {
        if (step.decider === 'supervisor' || step.title === 'Supervisor Approval') {
          return {
            ...step,
            status: 'rejected',
            who: `${userData?.first_name || ''} ${userData?.last_name || ''} (${userData?.primary_email || ''})`.trim(),
            completedAt: new Date().toISOString()
          };
        }
        return step;
      });
      
      // If supervisor step doesn't exist, add it
      if (!updatedProgress.find(step => step.decider === 'supervisor' || step.title === 'Supervisor Approval')) {
        updatedProgress.push({
          step: 2,
          decider: 'supervisor',
          title: 'Supervisor Approval',
          status: 'rejected',
          who: `${userData?.first_name || ''} ${userData?.last_name || ''} (${userData?.primary_email || ''})`.trim(),
          completedAt: new Date().toISOString()
        });
      }
      
      updateData.progress = updatedProgress;
      
      // Check if this is a decision change
      const existingSupervisorAction = (form.updatehistory || []).find(entry => 
        entry.action === "approved by supervisor" || entry.action === "denied by supervisor"
      );
      
      // Add denial entry to update history
      const modificationsDeny = computeModifications(updateData, originalWaiverData, form, flierPath, workDocsPaths);
      const denialEntry = {
        action: existingSupervisorAction ? "changed decision to denied by supervisor" : "denied by supervisor",
        timestamp: new Date().toISOString(),
        who: `${userData?.first_name || ''} ${userData?.last_name || ''} (${userData?.primary_email || ''})`.trim(),
        modifications: modificationsDeny
      };
      updateData.updatehistory = [
        denialEntry,
        ...(form.updatehistory || [])
      ];
      
      // Debug: log the update data being sent
      console.log('Update data being sent:', updateData);
      console.log('Supervisor signature in update data:', updateData.supervisor_signature);
      console.log('Supervisor signature length:', updateData.supervisor_signature?.length);
      
      // Send PATCH request
      await axios.patch(`/api/hourwaivers/${waiverId}`, updateData, {
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
      
      setSubmitSuccess(true);
      setOverlayMessage('✅ Denied successfully!');
      setOverlaySuccess(true);
      
      // Hide signature pad after successful denial so supervisor must sign again for next decision
      setSignaturePadHidden(true);
      setForm(f => ({ ...f, supervisor: { ...f.supervisor, signature: '' } }));
      if (signaturePadRef.current) {
        signaturePadRef.current.clear();
      }
      
      // Refresh the waiver data to show updated progress
      setTimeout(async () => {
        try {
          const res = await axios.get(`/api/hourwaivers/${waiverId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const updatedWaiver = res.data.hourwaiver;
          
          if (updatedWaiver) {
            // Update the form with fresh data
            setForm({
              ...initialForm,
              ...updatedWaiver,
              status: updatedWaiver.status || 'Submitted', // Explicitly preserve status
              date: new Date(updatedWaiver.date).toISOString().split('T')[0] || '',
              timeStart: updatedWaiver.timestart || '',
              timeEnd: updatedWaiver.timeend || '',
              concentrations: updatedWaiver.concentrations || [],
              workdocuments: updatedWaiver.workdocuments || [],
              flier: updatedWaiver.flier || null,
              supervisor: {
                firstName: updatedWaiver.supervisor_firstname || '',
                lastName: updatedWaiver.supervisor_lastname || '',
                title: updatedWaiver.supervisor_title || '',
                email: updatedWaiver.supervisor_email || '',
                phone: updatedWaiver.supervisor_phone || '',
                signature: updatedWaiver.supervisor_signature || ''
              },
              tor: updatedWaiver.tor || '',
              survey: updatedWaiver.surveydata || [],
              progress: updatedWaiver.progress || [],
              taxonomy: updatedWaiver.taxonomy || [],
              selectedConcentrations: updatedWaiver.selectedConcentrations || []
            });
            
            // Update original data for comparison
            setOriginalWaiverData(updatedWaiver);
            
            // Update selectedConcentrations with migration logic
            const migratedConcentrations = migrateConcentrations(updatedWaiver, concentrations);
            setSelectedConcentrations(migratedConcentrations);
            
            // Also ensure form.concentrations has the concentration IDs
            if (updatedWaiver.concentrations && Array.isArray(updatedWaiver.concentrations)) {
              setForm(f => ({ ...f, concentrations: updatedWaiver.concentrations }));
            } else if (migratedConcentrations.length > 0) {
              setForm(f => ({ ...f, concentrations: migratedConcentrations.map(c => c.id) }));
            }
          }
        } catch (err) {
          console.error('Error refreshing waiver data after denial:', err);
        }
        setShowOverlay(false);
      }, 1500);
      
    } catch (err) {
      console.error('Denial error:', err);
      
      if (err.response?.status === 409) {
        setSubmitError('The hour waiver was updated while you were working. Please reload the page to see the latest version.');
        setOverlayMessage('Version conflict detected. Please reload the page.');
        setOverlaySuccess(false);
      } else {
        setSubmitError(err.response?.data?.error || 'Denial failed. Please try again.');
        setOverlayMessage(err.response?.data?.error || 'Denial failed. Please try again.');
        setOverlaySuccess(false);
        setTimeout(() => setShowOverlay(false), 3000);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSave = async () => {
    // TODO: Implement save logic
    console.log('Save clicked');
  };

  const handleDoubleClickSubmit = async (e) => {
    e.preventDefault();
    await handleSubmit(e);
  };

  // Function to check if there are unsaved changes
  const checkForChanges = () => {
    if (!originalWaiverData || !waiverId) {
      setHasUnsavedChanges(false);
      return;
    }

    // Quick check for common changes
    const hasFormChanges = 
      form.type !== originalWaiverData.type ||
      form.hours !== originalWaiverData.hours ||
      form.date !== originalWaiverData.date ||
      form.timeStart !== originalWaiverData.timestart ||
      form.timeEnd !== originalWaiverData.timeend ||
      form.description !== originalWaiverData.description ||
      form.tor !== originalWaiverData.tor ||
      form.supervisor.firstName !== originalWaiverData.supervisor_firstname ||
      form.supervisor.lastName !== originalWaiverData.supervisor_lastname ||
      form.supervisor.title !== originalWaiverData.supervisor_title ||
      form.supervisor.email !== originalWaiverData.supervisor_email ||
      form.supervisor.phone !== originalWaiverData.supervisor_phone;

    const hasFileChanges = 
      (form.flier && form.flier instanceof File) ||
      (form.workdocuments && form.workdocuments.some(file => file instanceof File));

    setHasUnsavedChanges(hasFormChanges || hasFileChanges);
  };

  // Handler for supervisor confirmation
  const handleSupervisorConfirm = () => {
    if (supervisorInfo) {
      // Validate supervisor code
      if (!supervisorCode.trim()) {
        setSupervisorCodeError('Please enter the supervisor code');
        return;
      }
      
      // Check if the entered code matches the waiver's supervisor code
      if (supervisorCode.trim() !== (originalWaiverData?.supervisor_code || '').trim()) {
        setSupervisorCodeError('Invalid supervisor code');
        return;
      }
      
      // Create fake userData with supervisor info from the waiver
      const fakeUserData = {
        first_name: supervisorInfo.firstName,
        last_name: supervisorInfo.lastName,
        primary_email: supervisorInfo.email,
        accesscards: [{
          accesscard_id: 'supervisor-guest',
          email: supervisorInfo.email,
          role: 'Supervisor',
          school: { title: 'External Activity' }
        }],
        default_accesscard_id: 'supervisor-guest'
      };
      
      // Update the userData directly using the setter function
      setUserData(fakeUserData);
      
      // Close popup and clear code
      setShowSupervisorConfirm(false);
      setSupervisorCode('');
      setSupervisorCodeError('');
    }
  };

  const handleSupervisorDecline = () => {
    setShowSupervisorConfirm(false);
    setSupervisorCode('');
    setSupervisorCodeError('');
    // Redirect to login
    const returnTo = location.pathname + location.search + location.hash;
    navigate('/login', { state: { from: returnTo } });
  };

  // Function to get supervisor status message and color
  const getSupervisorStatusInfo = () => {
    // For new waivers (no waiverId), show email message
    if (!waiverId) {
      return {
        message: "Supervisor will be emailed to electronically sign this request.",
        color: '#b22222'
      };
    }
    
    // For existing waivers, check if supervisor has signed
    if (!form.supervisor_signature && !form.supervisor?.signature) {
      return {
        message: "Supervisor has yet to sign this request.",
        color: '#b22222'
      };
    }
    
    // Supervisor has signed, check their decision
    const updateHistory = form.updatehistory || [];
    const supervisorActions = updateHistory.filter(entry => 
      entry.action === "approved by supervisor" || 
      entry.action === "denied by supervisor" ||
      entry.action === "changed decision to approved by supervisor" ||
      entry.action === "changed decision to denied by supervisor"
    );
    
    if (supervisorActions.length > 0) {
      const mostRecentAction = supervisorActions[0]; // Most recent is first in array
      let decision;
      if (mostRecentAction.action === "approved by supervisor" || mostRecentAction.action === "changed decision to approved by supervisor") {
        decision = "approved";
      } else {
        decision = "denied";
      }
      const color = decision === "approved" ? '#4caf50' : '#b22222';
      return {
        message: `Supervisor has signed and ${decision} this request`,
        color: color
      };
    }
    
    // Fallback if signed but no clear decision found
    return {
      message: "Supervisor has signed this request",
      color: '#b22222'
    };
  };

  // Show loading while checking authentication
  if (authChecking) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255,255,255,0.85)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #1976d2',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: 24
        }} />
        <div style={{ fontSize: 20, fontWeight: 600, color: '#1976d2', textAlign: 'center' }}>
          Checking authentication...
        </div>
      </div>
    );
  }

  if (loadingStudentWaiver) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255,255,255,0.85)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #1976d2',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: 24
        }} />
        <div style={{ fontSize: 20, fontWeight: 600, color: '#1976d2', textAlign: 'center' }}>
          Loading hour waiver...
        </div>
      </div>
    );
  }



  // Show supervisor confirmation popup
  if (showSupervisorConfirm && supervisorInfo) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255,255,255,0.95)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
      }}>
        <div style={{
          background: '#fff',
          borderRadius: 12,
          padding: 32,
          boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
          textAlign: 'center',
          maxWidth: 500,
          border: '1px solid #e3e8f0'
        }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#1976d2', marginBottom: 16 }}>
            👋 Supervisor Access
          </div>
          <div style={{ fontSize: 18, color: '#333', marginBottom: 8, fontWeight: 600 }}>
            Are you {supervisorInfo.firstName} {supervisorInfo.lastName}?
          </div>
          <div style={{ fontSize: 16, color: '#666', marginBottom: 16, lineHeight: 1.5 }}>
            ({supervisorInfo.email})
          </div>
          <div style={{ fontSize: 14, color: '#666', marginBottom: 16, lineHeight: 1.5, fontStyle: 'italic' }}>
            Please enter the supervisor code to access the hour waiver form.
          </div>
          <div style={{ marginBottom: 24 }}>
            <input
              type="text"
              value={supervisorCode}
              onChange={(e) => {
                setSupervisorCode(e.target.value);
                setSupervisorCodeError('');
              }}
              placeholder="Enter supervisor code"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: supervisorCodeError ? '2px solid #d32f2f' : '2px solid #e3e8f0',
                borderRadius: 8,
                fontSize: 16,
                outline: 'none',
                transition: 'border-color 0.2s'
              }}
            />
            {supervisorCodeError && (
              <div style={{ color: '#d32f2f', fontSize: 14, marginTop: 8, textAlign: 'left' }}>
                {supervisorCodeError}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button
              onClick={handleSupervisorConfirm}
              style={{
                background: '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                padding: '12px 24px',
                fontSize: 16,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Confirm Access
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show role popup if user is not a student (for new waiver creation)
  if (showRolePopup && !studentView) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255,255,255,0.95)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
      }}>
        <div style={{
          background: '#fff',
          borderRadius: 12,
          padding: 32,
          boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
          textAlign: 'center',
          maxWidth: 500,
          border: '1px solid #e3e8f0'
        }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#d32f2f', marginBottom: 16 }}>
            ⚠️ Access Restricted
          </div>
          <div style={{ fontSize: 16, color: '#666', marginBottom: 24, lineHeight: 1.5 }}>
            You are not a student and will therefore not be able to submit this form
          </div>
        </div>
      </div>
    );
  }

  if (criticalError) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255,255,255,0.95)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
      }}>
        <div style={{
          background: '#fff',
          borderRadius: 12,
          padding: 32,
          boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
          textAlign: 'center',
          maxWidth: 500,
          border: '1px solid #e3e8f0'
        }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#d32f2f', marginBottom: 16 }}>
            {criticalErrorTitle}
          </div>
          <div style={{ fontSize: 16, color: '#666', marginBottom: 24, lineHeight: 1.5 }}>
            {criticalError}
          </div>
          {criticalErrorShowButton && (
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                          <button
              onClick={() => criticalErrorPath === 'reload' ? window.location.reload() : navigate(criticalErrorPath)}
              style={{
                background: '#d32f2f',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                padding: '12px 24px',
                fontSize: 16,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              {criticalErrorButtonText}
            </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Function to map command to standardized role name
  const getStandardizedRole = (command) => {
    const roleMap = {
      'student': 'Student',
      'tor': 'TOR',
      'schoolAdmin': 'School Admin',
      'systemAdmin': 'System Admin',
      'supervisor': 'Student' // Supervisor maps to Student for relevantRole
    };
    return roleMap[command] || 'Student';
  };

  return (
    <div style={{ 
      padding: '2rem', 
      paddingBottom: isFormEditable ? '140px' : '2rem', // Extra bottom padding for sticky button
      maxWidth: 900, 
      margin: '0 auto', 
      fontFamily: 'inherit',
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      minHeight: '100vh'
    }}>
      {/* Verified Hours Banner */}
      {form.status === 'Verified' && (
        <div style={{
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          color: 'white',
          textAlign: 'center',
          padding: '20px 32px',
          borderRadius: '16px',
          marginBottom: '32px',
          boxShadow: '0 8px 25px rgba(16, 185, 129, 0.25)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          backdropFilter: 'blur(10px)'
        }}>
          <div style={{ fontSize: '28px', fontWeight: '700', marginBottom: '8px' }}>
            ✅ {form.hours} Hours Verified
          </div>
          <div style={{ fontSize: '16px', opacity: 0.95, fontWeight: 500 }}>
            This request has been officially approved and verified by the system administrator
          </div>
        </div>
      )}
      
      {/* Modern Header */}
      <div style={{ 
        textAlign: 'center', 
        marginBottom: '32px',
        padding: '24px 20px',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        borderRadius: '12px',
        color: '#475569',
        border: '1px solid #e2e8f0',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Menu Buttons */}
        <div style={{
          position: 'absolute',
          top: '16px',
          right: '16px',
          display: 'flex',
          gap: '8px',
          zIndex: 2
        }}>
          {/* Chat Button */}
          {waiverId && (
            <button
              style={{
                padding: '2px',
                background: 'rgba(255, 255, 255, 0.9)',
                border: '2px solid #1D4E89',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(30,64,175,0.15)',
                transition: 'all 0.2s ease',
                width: '40px',
                height: '40px'
              }}
              onClick={() => setShowComments(true)}
              onMouseOver={e => {
                e.currentTarget.style.transform = 'scale(1.05)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(30,64,175,0.25)';
              }}
              onMouseOut={e => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(30,64,175,0.15)';
              }}
              title='Chat'
            >
              <svg width="34" height="34" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 16C12 13.7909 13.7909 12 16 12H32C34.2091 12 36 13.7909 36 16V26C36 28.2091 34.2091 30 32 30H22L16 36V30H16C13.7909 30 12 28.2091 12 26V16Z" fill="#1D4E89" stroke="#1D4E89" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="18" cy="21" r="1.5" fill="white"/>
                <circle cx="24" cy="21" r="1.5" fill="white"/>
                <circle cx="30" cy="21" r="1.5" fill="white"/>
              </svg>
            </button>
          )}
          
          {/* Revisions Button */}
          {waiverId && form.updatehistory && form.updatehistory.length > 0 && (
            <button
              style={{
                padding: '2px',
                background: 'rgba(255, 255, 255, 0.9)',
                border: '2px solid #6366f1',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(99, 102, 241, 0.15)',
                transition: 'all 0.2s ease',
                width: '40px',
                height: '40px'
              }}
              onClick={() => setShowRevisionsModal(true)}
              onMouseOver={e => {
                e.currentTarget.style.transform = 'scale(1.05)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(99, 102, 241, 0.25)';
              }}
              onMouseOut={e => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(99, 102, 241, 0.15)';
              }}
              title={`View Revision History (${form.updatehistory.length})`}
            >
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9 9H9.01M15 9H15.01M21 9H21.01" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}
        </div>

        <h2 style={{ 
          marginBottom: '6px', 
          fontWeight: 600, 
          letterSpacing: 0.5, 
          fontSize: '28px',
          position: 'relative',
          zIndex: 1,
          color: '#334155'
        }}>
          Request for CTE Credit Hours
        </h2>
        <div style={{ 
          fontStyle: 'italic', 
          fontSize: '16px', 
          opacity: 0.8,
          fontWeight: 400,
          position: 'relative',
          zIndex: 1,
          color: '#64748b'
        }}>
          by Approved External Activities
        </div>
      </div>
      
      {/* Approval Progress - Only show in student view */}
      {studentView && (
        <ApprovalProgress
          progress={form.progress || []}
          requiredSteps={requiredSteps}
          currentUserRole={currentUserRole}
          onApprove={handleApprove}
          onDeny={handleDeny}
          updateHistory={form.updatehistory || []}
          originalWaiverData={originalWaiverData}
          minimized={form.status === 'Verified'}
          renderSupervisorActions={() => {
            // Get current decision for the current user's role
            const currentUserProgress = (form.progress || []).find(p => p.decider === currentUserRole || p.title === requiredSteps.find(s => s.decider === currentUserRole)?.title);
            const currentDecision = currentUserProgress ? currentUserProgress.status : null;
            
            // Determine which handlers to use based on role
            let onApprove, onDeny;
            if (currentUserRole === 'supervisor') {
              onApprove = () => {
                if (!form.supervisor.signature) {
                  setSignatureError('Signature required to approve.');
                  setSignaturePadHidden(false);
                  return;
                }
                if (currentDecision === 'complete') {
                  return;
                }
                setSignatureError('');
                handleApprove();
              };
              onDeny = () => {
                if (!form.supervisor.signature) {
                  setSignatureError('Signature required to deny.');
                  setSignaturePadHidden(false);
                  return;
                }
                if (currentDecision === 'rejected') {
                  return;
                }
                setSignatureError('');
                handleDeny();
              };
            } else if (currentUserRole === 'schoolAdmin') {
              onApprove = () => {
                if (currentDecision === 'complete') {
                  return;
                }
                handleSchoolAdminApprove();
              };
              onDeny = () => {
                if (currentDecision === 'rejected') {
                  return;
                }
                handleSchoolAdminDeny();
              };
            } else if (currentUserRole === 'systemAdmin') {
              onApprove = () => {
                if (currentDecision === 'complete') {
                  return;
                }
                handleSystemAdminApprove();
              };
              onDeny = () => {
                if (currentDecision === 'rejected') {
                  return;
                }
                handleSystemAdminDeny();
              };
            }
            
            return (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                <div style={{ display: 'flex', flexDirection: 'row', gap: 10, justifyContent: 'center', width: '100%' }}>
                  <button
                    type="button"
                    style={{
                      background: currentDecision === 'complete' ? '#ccc' : '#4caf50',
                      color: 'white',
                      fontWeight: 700,
                      border: 'none',
                      borderRadius: 6,
                      padding: '8px 18px',
                      fontSize: 15,
                      cursor: (currentUserRole === 'supervisor' ? (form.supervisor.signature && currentDecision !== 'complete') : currentDecision !== 'complete') ? 'pointer' : 'not-allowed',
                      opacity: (currentUserRole === 'supervisor' ? (form.supervisor.signature && currentDecision !== 'complete') : currentDecision !== 'complete') ? 1 : 0.6,
                      minWidth: 100,
                      maxWidth: 180,
                    }}
                    disabled={(currentUserRole === 'supervisor' && !form.supervisor.signature) || currentDecision === 'complete'}
                    onClick={onApprove}
                  >APPROVE</button>
                  <button
                    type="button"
                    style={{
                      background: currentDecision === 'rejected' ? '#ccc' : '#f44336',
                      color: 'white',
                      fontWeight: 700,
                      border: 'none',
                      borderRadius: 6,
                      padding: '8px 18px',
                      fontSize: 15,
                      cursor: (currentUserRole === 'supervisor' ? (form.supervisor.signature && currentDecision !== 'rejected') : currentDecision !== 'rejected') ? 'pointer' : 'not-allowed',
                      opacity: (currentUserRole === 'supervisor' ? (form.supervisor.signature && currentDecision !== 'rejected') : currentDecision !== 'rejected') ? 1 : 0.6,
                      minWidth: 100,
                      maxWidth: 180,
                    }}
                    disabled={(currentUserRole === 'supervisor' && !form.supervisor.signature) || currentDecision === 'rejected'}
                    onClick={onDeny}
                  >DENY</button>
                </div>
                {currentUserRole === 'supervisor' && !form.supervisor.signature && (
                  <div style={{ width: '80%', color: '#b22222', fontSize: 10, marginTop: 0, textAlign: 'center', fontStyle: 'italic' }}>
                    You must sign in the supervisor section before approving or denying.
                  </div>
                )}
                {(currentDecision === 'complete' || currentDecision === 'rejected') && (
                  <div style={{ width: '80%', color: '#666', fontSize: 10, marginTop: 4, textAlign: 'center', fontStyle: 'italic' }}>
                    Current decision: {currentDecision === 'complete' ? 'APPROVED' : 'DENIED'}. Click the other button to change.
                  </div>
                )}
              </div>
            );
          }}
        />
      )}
      {/* Student Info */}
      <div style={{ 
        fontWeight: 700, 
        fontSize: '24px', 
        color: '#374151', 
        marginBottom: '16px', 
        marginLeft: 2,
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <div style={{
          width: '4px',
          height: '24px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '2px'
        }} />
        Student Information
      </div>
      <div style={{ 
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)', 
        borderRadius: '20px', 
        padding: '28px', 
        marginBottom: '40px', 
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)', 
        border: '1px solid rgba(102, 126, 234, 0.1)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '100px',
          height: '100px',
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
          borderRadius: '50%',
          transform: 'translate(30px, -30px)'
        }} />
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          fontWeight: 700, 
          fontSize: '20px', 
          color: '#374151',
          position: 'relative',
          zIndex: 1
        }}>
          <span>{studentDisplay}</span>
        </div>
        <div style={{ 
          marginTop: '12px', 
          color: '#6b7280', 
          fontSize: '16px',
          fontWeight: 500,
          position: 'relative',
          zIndex: 1
        }}>
          {gradeSchoolDisplay}
        </div>
      </div>
      <form onSubmit={handleSubmit} style={{ 
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)', 
        borderRadius: '24px', 
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)', 
        padding: '40px',
        position: 'relative',
        opacity: isFormEditable ? 1 : 0.7,
        transition: 'opacity 0.3s ease',
        border: '1px solid rgba(102, 126, 234, 0.08)'
      }}>
        {/* Disabled overlay */}
        {!isFormEditable && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: 18,
            pointerEvents: 'none',
            zIndex: 1
          }} />
        )}
        
        {/* Form disabled message */}
        {!isFormEditable && (
          <div style={{
            background: '#fff3cd',
            border: '1px solid #ffeaa7',
            color: '#856404',
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '24px',
            fontSize: '14px',
            fontWeight: '500',
            textAlign: 'center'
          }}>
            <strong>Form is read-only</strong> - You can view the information but cannot make changes. 
            Check the Approval Progress panel for details on why the form is disabled.
          </div>
        )}
        
        {/* Activity Details */}
        <div style={{ 
          fontWeight: 700, 
          fontSize: '24px', 
          color: '#374151', 
          marginBottom: '16px', 
          marginLeft: 2,
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <div style={{
            width: '4px',
            height: '24px',
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            borderRadius: '2px'
          }} />
          Activity Details
        </div>
        <div style={{ 
          border: 'none', 
          borderRadius: '20px', 
          padding: '32px', 
          marginBottom: '40px', 
          background: 'linear-gradient(135deg, #ffffff 0%, #fefefe 100%)', 
          boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
          border: '1px solid rgba(245, 158, 11, 0.1)'
        }}>

          <div style={{ display: 'flex', gap: 18, marginBottom: 18 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontWeight: 600, color: '#374151', fontSize: '14px' }}>Date of Activity*<br />
                <input 
                  type="date" 
                  name="date" 
                  value={form.date} 
                  onChange={handleChange} 
                  style={{ 
                    width: '100%', 
                    border: '2px solid #e5e7eb', 
                    borderRadius: '12px', 
                    padding: '12px 16px', 
                    fontSize: '16px', 
                    marginTop: '8px',
                    background: '#ffffff',
                    transition: 'all 0.2s ease',
                    outline: 'none'
                  }} 
                  disabled={!isFormEditable}
                  onFocus={(e) => e.target.style.borderColor = '#f59e0b'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </label>
              {errors.date && <div style={{ color: '#ef4444', fontSize: '13px', marginTop: '4px', fontWeight: 500 }}>{errors.date}</div>}
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontWeight: 600, color: '#374151', fontSize: '14px' }}>Start Time*<br />
                <input 
                  type="time" 
                  name="timeStart" 
                  value={form.timeStart} 
                  onChange={handleChange} 
                  style={{ 
                    width: '100%', 
                    border: '2px solid #e5e7eb', 
                    borderRadius: '12px', 
                    padding: '12px 16px', 
                    fontSize: '16px', 
                    marginTop: '8px',
                    background: '#ffffff',
                    transition: 'all 0.2s ease',
                    outline: 'none'
                  }} 
                  disabled={!isFormEditable}
                  onFocus={(e) => e.target.style.borderColor = '#f59e0b'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </label>
              {errors.timeStart && <div style={{ color: '#ef4444', fontSize: '13px', marginTop: '4px', fontWeight: 500 }}>{errors.timeStart}</div>}
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontWeight: 600, color: '#374151', fontSize: '14px' }}>End Time*<br />
                <input 
                  type="time" 
                  name="timeEnd" 
                  value={form.timeEnd} 
                  onChange={handleChange} 
                  style={{ 
                    width: '100%', 
                    border: '2px solid #e5e7eb', 
                    borderRadius: '12px', 
                    padding: '12px 16px', 
                    fontSize: '16px', 
                    marginTop: '8px',
                    background: '#ffffff',
                    transition: 'all 0.2s ease',
                    outline: 'none'
                  }} 
                  disabled={!isFormEditable}
                  onFocus={(e) => e.target.style.borderColor = '#f59e0b'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </label>
              {errors.timeEnd && <div style={{ color: '#ef4444', fontSize: '13px', marginTop: '4px', fontWeight: 500 }}>{errors.timeEnd}</div>}
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>Hours Requested*<br />
                <input 
                  type="number" 
                  name="hours" 
                  value={form.hours} 
                  onChange={handleChange} 
                  min="0" 
                  step="0.1" 
                  style={{ 
                    width: '100%', 
                    border: '2px solid #e5e7eb', 
                    borderRadius: '12px', 
                    padding: '12px 16px', 
                    fontSize: '16px', 
                    marginTop: '8px',
                    background: '#ffffff',
                    transition: 'all 0.2s ease',
                    outline: 'none'
                  }} 
                  disabled={!isFormEditable}
                  onFocus={(e) => e.target.style.borderColor = '#f59e0b'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </label>
              {errors.hours && <div style={{ color: '#ef4444', fontSize: '13px', marginTop: '4px', fontWeight: 500 }}>{errors.hours}</div>}
            </div>
          </div>
          <div style={{ marginBottom: 18 }}>
            <label style={{ fontWeight: 600, color: '#374151', fontSize: '14px' }}>Description of the Activity*<br />
              <textarea 
                name="description" 
                value={form.description} 
                onChange={handleChange} 
                rows={4} 
                style={{ 
                  width: '100%', 
                  border: '2px solid #e5e7eb', 
                  borderRadius: '12px', 
                  padding: '16px', 
                  fontSize: '16px', 
                  marginTop: '8px',
                  background: '#ffffff',
                  transition: 'all 0.2s ease',
                  outline: 'none',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }} 
                disabled={!isFormEditable}
                onFocus={(e) => e.target.style.borderColor = '#f59e0b'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                placeholder="Describe the activity in detail..."
              />
            </label>
            {errors.description && <div style={{ color: '#ef4444', fontSize: '13px', marginTop: '4px', fontWeight: 500 }}>{errors.description}</div>}
          </div>
          
          {/* Taxonomy Selection */}
          <div style={{ marginBottom: 18 }}>
            <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: 12, color: '#374151' }}>Concentrations, Skills, Standards, Objectives</h2>
            <div style={{ color: '#1D4E89', fontSize: '0.98rem', marginBottom: 12, fontWeight: 500 }}>
              Select other concentrations (if any) involved in this activity. Also select any relevant skills, standards, or objectives.
            </div>
            <TaxonomySelector
              selectedTaxonomy={form.taxonomy}
              onTaxonomyChange={(taxonomy) => setForm(f => ({ ...f, taxonomy }))}
              concentrations={concentrations}
              disabled={!isFormEditable}
              primaryConcentration={form.concentrations[0] || null}
              selectedConcentrations={form.concentrations}
              isStudentRole={isStudentRole}
              showPrimaryBehavior={false}
              onSelectedConcentrationsChange={(concentrationIds) => {
                setForm(f => ({ ...f, concentrations: concentrationIds }));
                // Also update selectedConcentrations for display purposes
                const selectedConcentrationObjects = concentrationIds.map(id => {
                  const concentration = concentrations.find(c => c.id === id);
                  return concentration ? { id: concentration.id, title: concentration.title } : null;
                }).filter(Boolean);
                setSelectedConcentrations(selectedConcentrationObjects);
              }}
              taxonomyItems={taxonomyItems}
            />
            
            {/* Other Concentration Option */}
            <div style={{ marginTop: 16 }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 8, 
                marginBottom: 8,
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  name="hasOtherConcentration"
                  checked={form.hasOtherConcentration}
                  onChange={handleChange}
                  style={{ accentColor: '#f59e0b', width: 20, height: 20 }}
                  disabled={!isFormEditable}
                />
                <span style={{ color: '#374151', fontWeight: 600, fontSize: '16px' }}>Other Concentration</span>
              </label>
              {form.hasOtherConcentration && (
                <div style={{ marginTop: 8 }}>
                  <input
                    type="text"
                    name="otherConcentration"
                    value={form.otherConcentration}
                    onChange={handleChange}
                    placeholder="Specify other concentration"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '12px',
                      fontSize: '16px',
                      outline: 'none',
                      fontFamily: 'inherit',
                      transition: 'border-color 0.2s ease'
                    }}
                    disabled={!isFormEditable}
                    onFocus={(e) => e.target.style.borderColor = '#f59e0b'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                  {errors.otherConcentration && <div style={{ color: '#ef4444', fontSize: '13px', marginTop: '4px', fontWeight: 500 }}>{errors.otherConcentration}</div>}
                </div>
              )}
            </div>
          </div>

          {/* Removed the block rendering the Skills & Standards container below the checkboxes */}
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontWeight: 600, marginBottom: 6, color: '#283593' }}>What type of hours are you requesting?</div>
            <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
              {hourTypes.map(t => (
                <label key={t} style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 500, color: '#3949ab' }}>
                  <input
                    type="radio"
                    name="type"
                    value={t}
                    checked={form.type === t}
                    onChange={handleChange}
                    style={{ accentColor: '#3949ab', width: 18, height: 18 }}
                    disabled={!isFormEditable}
                  />
                  {t}
                </label>
              ))}
              <button type="button" style={{ marginLeft: 16, background: '#e3eafc', border: 'none', borderRadius: 8, padding: '4px 16px', cursor: 'pointer', color: '#1976d2', fontWeight: 700, fontSize: 15, boxShadow: '0 1px 4px rgba(60,72,100,0.07)' }} onClick={() => setShowHourTypeInfo(v => !v)}>
                What do these mean?
              </button>
            </div>
            {showHourTypeInfo && (
              <div style={{ background: '#f5faff', border: '1px solid #b3d1f7', borderRadius: 8, padding: 12, marginTop: 8, color: '#1976d2', fontSize: 15 }}>
                {hourTypeDescriptions ? (
                  <div dangerouslySetInnerHTML={{ __html: hourTypeDescriptions }} />
                ) : (
                  <div>Loading descriptions...</div>
                )}
              </div>
            )}
          </div>
        </div>
        {/* Activity Documents */}
        <div style={{
          fontWeight: 700,
          fontSize: '24px',
          color: '#374151',
          marginBottom: '16px',
          marginLeft: 2,
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <div style={{
            width: '4px',
            height: '24px',
            background: 'linear-gradient(135deg, #38bdf8 0%, #0ea5e9 100%)',
            borderRadius: '2px'
          }} />
          Activity Documents
        </div>
        <div style={{
          border: 'none',
          borderRadius: '20px',
          padding: '32px',
          marginBottom: '40px',
          background: 'linear-gradient(135deg, #f8fafc 0%, #fff 100%)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
          border: '1px solid rgba(14, 165, 233, 0.08)'
        }}>
          <div style={{ marginBottom: 24 }}>
            <label style={{ fontWeight: 600, color: '#374151', fontSize: '15px' }}>Activity Flier* (Required):<br />
              {/* Show existing flier if available */}
              {waiverId && form.flier && form.flier !== '[File: ]' && (
                <div style={{ marginTop: 12, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <button
                    type="button"
                    onClick={() => window.open(form.flier, '_blank')}
                    style={{
                      background: 'linear-gradient(135deg, #38bdf8 0%, #0ea5e9 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: 8,
                      padding: '6px 18px',
                      fontSize: 14,
                      cursor: 'pointer',
                      fontWeight: 600,
                      boxShadow: '0 2px 8px rgba(14, 165, 233, 0.10)',
                      transition: 'all 0.2s'
                    }}
                  >
                    View Flier
                  </button>
                  {isFormEditable && (
                    <span style={{ color: '#f59e0b', fontSize: 13, fontWeight: 500 }}>
                      If you choose to upload again, any current files will be removed.
                    </span>
                  )}
                </div>
              )}
              {/* Show upload input if editable */}
              {isFormEditable && (
                <input type="file" name="flier" accept="application/pdf,image/*" onChange={handleFileChange} style={{ marginTop: 8, fontSize: 15, borderRadius: 8, padding: '8px', border: '1px solid #e5e7eb', background: '#f9fafb' }} />
              )}
              {/* Show "no flier" message if no flier exists and not editable */}
              {!isFormEditable && (!form.flier || form.flier === '[File: ]') && (
                <div style={{ marginTop: 8 }}>
                  <span style={{ color: '#6b7280', fontStyle: 'italic' }}>No flier uploaded</span>
                </div>
              )}
            </label>
            {errors.flier && <div style={{ color: '#ef4444', fontSize: 13, marginTop: 4 }}>{errors.flier}</div>}
          </div>
          <div style={{ marginBottom: 24 }}>
            <label style={{ fontWeight: 600, color: '#374151', fontSize: '15px' }}>Work Documents* (Required):<br />
              {/* Show existing work documents if available */}
              {waiverId && form.workdocuments && form.workdocuments.length > 0 && form.workdocuments.some(doc => doc && doc !== '[File: ]') && (
                <div style={{ marginTop: 12, marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    {form.workdocuments.filter(doc => doc && doc !== '[File: ]').map((doc, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => window.open(doc, '_blank')}
                        style={{
                          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: 8,
                          padding: '6px 18px',
                          fontSize: 14,
                          cursor: 'pointer',
                          fontWeight: 600,
                          boxShadow: '0 2px 8px rgba(16, 185, 129, 0.10)',
                          transition: 'all 0.2s'
                        }}
                      >
                        View Doc {index + 1}
                      </button>
                    ))}
                  </div>
                  {isFormEditable && (
                    <span style={{ color: '#f59e0b', fontSize: 13, fontWeight: 500, marginTop: 4, display: 'block' }}>
                      If you choose to upload again, any current files will be removed.
                    </span>
                  )}
                </div>
              )}
              {/* Show upload input if editable */}
              {isFormEditable && (
                <input type="file" name="workdocuments" multiple accept="application/pdf,image/*" onChange={handleFileChange} style={{ marginTop: 8, fontSize: 15, borderRadius: 8, padding: '8px', border: '1px solid #e5e7eb', background: '#f9fafb' }} />
              )}
              {/* Show "no work documents" message if none exist and not editable */}
              {!isFormEditable && (!form.workdocuments || form.workdocuments.length === 0 || !form.workdocuments.some(doc => doc && doc !== '[File: ]')) && (
                <div style={{ marginTop: 8 }}>
                  <span style={{ color: '#6b7280', fontStyle: 'italic' }}>No work documents uploaded</span>
                </div>
              )}
            </label>
            {errors.workdocuments && <div style={{ color: '#ef4444', fontSize: 13, marginTop: 4 }}>{errors.workdocuments}</div>}
          </div>
        </div>

        {/* Supervisor of Activity */}
        <div style={{
          fontWeight: 700,
          fontSize: '24px',
          color: '#374151',
          marginBottom: '16px',
          marginLeft: 2,
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <div style={{
            width: '4px',
            height: '24px',
            background: 'linear-gradient(135deg, #f472b6 0%, #a21caf 100%)',
            borderRadius: '2px'
          }} />
          Supervisor of Activity
        </div>
        <div style={{
          border: 'none',
          borderRadius: '20px',
          padding: '32px',
          marginBottom: '40px',
          background: 'linear-gradient(135deg, #fdf2f8 0%, #fff 100%)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
          border: '1px solid rgba(236, 72, 153, 0.08)'
        }}>
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginBottom: 18 }}>
            <div style={{ flex: 1, minWidth: 180 }}>
              <label style={{ fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Supervisor's First Name*:<br />
                <input
                  type="text"
                  name="supervisor.firstName"
                  value={form.supervisor.firstName}
                  onChange={handleChange}
                  placeholder="Enter first name"
                  style={{ width: '100%', border: '2px solid #e5e7eb', borderRadius: 12, padding: 12, fontSize: 16, background: '#fff', marginTop: 6, fontWeight: 500 }}
                  disabled={!isFormEditable}
                />
              </label>
              {errors.supervisorFirstName && <div style={{ color: '#ef4444', fontSize: 13 }}>{errors.supervisorFirstName}</div>}
            </div>
            <div style={{ flex: 1, minWidth: 180 }}>
              <label style={{ fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Supervisor's Last Name*:<br />
                <input
                  type="text"
                  name="supervisor.lastName"
                  value={form.supervisor.lastName}
                  onChange={handleChange}
                  placeholder="Enter last name"
                  style={{ width: '100%', border: '2px solid #e5e7eb', borderRadius: 12, padding: 12, fontSize: 16, background: '#fff', marginTop: 6, fontWeight: 500 }}
                  disabled={!isFormEditable}
                />
              </label>
              {errors.supervisorLastName && <div style={{ color: '#ef4444', fontSize: 13 }}>{errors.supervisorLastName}</div>}
            </div>
            <div style={{ flex: 1, minWidth: 180 }}>
              <label style={{ fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Supervisor's Title*:<br />
                <input
                  type="text"
                  name="supervisor.title"
                  value={form.supervisor.title}
                  onChange={handleChange}
                  placeholder="Enter job title"
                  style={{ width: '100%', border: '2px solid #e5e7eb', borderRadius: 12, padding: 12, fontSize: 16, background: '#fff', marginTop: 6, fontWeight: 500 }}
                  disabled={!isFormEditable}
                />
              </label>
              {errors.supervisorTitle && <div style={{ color: '#ef4444', fontSize: 13 }}>{errors.supervisorTitle}</div>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginBottom: 18 }}>
            <div style={{ flex: 1, minWidth: 180 }}>
              <label style={{ fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Supervisor's Phone Number*:<br />
                <input
                  type="text"
                  name="supervisor.phone"
                  value={form.supervisor.phone}
                  onChange={handleChange}
                  placeholder="Enter phone number"
                  style={{ width: '100%', border: '2px solid #e5e7eb', borderRadius: 12, padding: 12, fontSize: 16, background: '#fff', marginTop: 6, fontWeight: 500 }}
                  disabled={!isFormEditable}
                />
              </label>
              {errors.supervisorPhone && <div style={{ color: '#ef4444', fontSize: 13 }}>{errors.supervisorPhone}</div>}
            </div>
            <div style={{ flex: 1, minWidth: 180 }}>
              <label style={{ fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Supervisor's Email*:<br />
                <input
                  type="email"
                  name="supervisor.email"
                  value={form.supervisor.email}
                  onChange={handleChange}
                  placeholder="Enter email address"
                  style={{ width: '100%', border: '2px solid #e5e7eb', borderRadius: 12, padding: 12, fontSize: 16, background: '#fff', marginTop: 6, fontWeight: 500 }}
                  disabled={!isFormEditable}
                />
              </label>
              {errors.supervisorEmail && <div style={{ color: '#ef4444', fontSize: 13 }}>{errors.supervisorEmail}</div>}
            </div>
          </div>
          {/* Signature Pad for Supervisor */}
          {currentUserRole === 'supervisor' && isFormEditable && (() => {
            // Hide signature pad if supervisor_signature exists and a supervisor decision is present in progress
            const hasSupervisorSignature = !!form.supervisor_signature;
            const supervisorDecision = (form.progress || []).find(
              p => (p.decider === 'supervisor' || p.title === 'Supervisor Approval') && (p.status === 'complete' || p.status === 'rejected')
            );
            if (hasSupervisorSignature && supervisorDecision) {
              return null;
            }
            return (
              <div style={{ marginTop: 24, marginBottom: 12 }}>
                <div style={{ fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                  Supervisor Signature*:
                </div>
                {/* Show signature image if present and pad is hidden */}
                {form.supervisor.signature && signaturePadHidden ? (
                  <div style={{ marginBottom: 8 }}>
                    <img
                      src={form.supervisor.signature}
                      alt="Supervisor Signature"
                      style={{ border: '2px solid #e5e7eb', borderRadius: 8, width: 400, height: 120, objectFit: 'contain', background: '#fff' }}
                    />
                    <div style={{ color: '#6b7280', fontSize: 13, marginTop: 4 }}>
                      Signature saved. You may clear and re-sign if needed.
                    </div>
                  </div>
                ) : (
                  <>
                    <SignatureCanvas
                      ref={signaturePadRef}
                      penColor="#1976d2"
                      backgroundColor="#fff"
                      canvasProps={{
                        width: 400,
                        height: 120,
                        style: { border: '2px solid #e5e7eb', borderRadius: 8 }
                      }}
                      onEnd={() => {
                        const signatureData = signaturePadRef.current.toDataURL('image/png');
                        setForm(f => ({
                          ...f,
                          supervisor: {
                            ...f.supervisor,
                            signature: signatureData
                          }
                        }));
                        setSignatureError('');
                      }}
                      clearOnResize={false}
                    />
                    <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                      <button
                        type="button"
                        onClick={() => {
                          signaturePadRef.current.clear();
                          setForm(f => ({
                            ...f,
                            supervisor: {
                              ...f.supervisor,
                              signature: ''
                            }
                          }));
                        }}
                        style={{
                          background: '#f3f4f6',
                          color: '#374151',
                          border: '1px solid #e5e7eb',
                          borderRadius: 8,
                          padding: '6px 18px',
                          fontWeight: 600,
                          cursor: 'pointer'
                        }}
                      >
                        Clear
                      </button>
                    </div>
                  </>
                )}
                {signatureError && (
                  <div style={{ color: '#d32f2f', fontSize: 13, marginTop: 4 }}>
                    {signatureError}
                  </div>
                )}
              </div>
            );
          })()}
          {currentUserRole !== 'supervisor' && (
            <div style={{ color: '#f59e0b', fontWeight: 600, marginBottom: 12, fontSize: 15 }}>
              {getSupervisorStatusInfo().message}
            </div>
          )}
          {showTOR && (
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontWeight: 600, color: '#374151' }}>Teacher of Record / Educational Coordinator*:<br />
                <select name="tor" value={form.tor} onChange={handleChange} style={{ minWidth: 220, border: '2px solid #e5e7eb', borderRadius: 12, padding: 12, fontSize: 16, marginTop: 6, background: '#fff', fontWeight: 500 }} required={showTOR} disabled={!isFormEditable}>
                {torOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </label>
              {errors.tor && <div style={{ color: '#ef4444', fontSize: 13 }}>{errors.tor}</div>}
          </div>
          )}
        </div>
        {/* Survey Portion */}
        <div style={{ border: '1px solid #1976d2', borderRadius: 8, padding: 16, marginBottom: 0, background: '#f5faff' }}>
          {surveyLoading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>Loading survey...</div>
          ) : systemAssignment ? (
            <AssignmentViewer
              ref={assignmentViewerRef}
              assignment={systemAssignment}
              mode="survey"
              interactive={isFormEditable}
              onSurveySubmit={() => {}}
              surveyData={form.survey}
              onSurveyDataChange={isFormEditable ? (newSurveyData) => {
                console.log('AssignmentViewer onSurveyDataChange:', newSurveyData);
                setForm(prev => ({ ...prev, survey: newSurveyData }));
              } : undefined}
              hideSubmitButton={true}
              submission={studentView ? {
                submissiondata: form.survey
              } : undefined}
                />
              ) : (
            <div style={{ color: 'red', textAlign: 'center', padding: '1rem' }}>
              Unable to load survey. Please refresh the page and try again.
                </div>
              )}
            </div>
                {/* Error messages remain in form */}
            {waiverId && !hasUnsavedChanges && isFormEditable && (
              <div style={{ textAlign: 'center', marginTop: '32px', color: '#6b7280', fontSize: '14px', fontStyle: 'italic' }}>
                No changes detected
              </div>
            )}
            {!waiverId && (!isStudentRole || !validEnrollment) && roleValidationError && isFormEditable && (
              <div style={{ textAlign: 'center', marginTop: '32px', color: '#ef4444', fontSize: '14px', fontWeight: 500 }}>
                {roleValidationError}
              </div>
            )}
        {submitSuccess && (
          <div style={{ color: 'green', marginTop: 16 }}>
            Submitted successfully!
            {/* Removed any preview or form display after submission */}
          </div>
        )}
          {submitError && <div style={{ color: 'red', marginTop: 16 }}>{submitError}</div>}

        {showOverlay && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255,255,255,0.85)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              border: '4px solid #f3f3f3',
              borderTop: overlaySuccess ? '4px solid #4caf50' : '4px solid #1976d2',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              marginBottom: 24
            }} />
            <div style={{ fontSize: 20, fontWeight: 600, color: overlaySuccess ? '#4caf50' : '#1976d2', textAlign: 'center' }}>
              {overlayMessage}
        </div>
          </div>
        )}

        {studentInfoError && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255,255,255,0.95)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
          }}>
            <div style={{
              background: '#fff',
              borderRadius: 12,
              padding: 32,
              boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
              textAlign: 'center',
              maxWidth: 400,
              border: '1px solid #e3e8f0'
            }}>
              <div style={{ fontSize: 24, fontWeight: 700, color: '#d32f2f', marginBottom: 16 }}>
                ⚠️ Error Loading Data
              </div>
              <div style={{ fontSize: 16, color: '#666', marginBottom: 24, lineHeight: 1.5 }}>
                {studentInfoError}
              </div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                <button
                  onClick={() => window.location.reload()}
                  style={{
                    background: '#1976d2',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    padding: '12px 24px',
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Reload Page
                </button>
                <button
                  onClick={() => setStudentInfoError(null)}
                  style={{
                    background: '#f5f5f5',
                    color: '#666',
                    border: '1px solid #ddd',
                    borderRadius: 8,
                    padding: '12px 24px',
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Continue Anyway
                </button>
              </div>
            </div>
          </div>
        )}
      </form>

      {showComments && (
        <ProjectComments
          referenceId={waiverId}
          table='hourwaivers'
          userData={userData}
          isOpen={showComments}
          onClose={() => setShowComments(false)}
          relevantRole={getStandardizedRole(currentUserRole || (userData && userData.accesscards && userData.default_accesscard_id && userData.accesscards.find(ac => ac.accesscard_id === userData.default_accesscard_id)?.role) || 'Student')}
        />
      )}

      {/* Revisions Modal */}
      {showRevisionsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '800px',
            width: '100%',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px',
              borderBottom: '2px solid #f3f4f6',
              paddingBottom: '16px'
            }}>
              <h2 style={{
                margin: 0,
                fontSize: '24px',
                fontWeight: 700,
                color: '#1f2937',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                📋 Revision History
              </h2>
              <button
                onClick={() => setShowRevisionsModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '4px',
                  borderRadius: '4px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f3f4f6';
                  e.target.style.color = '#374151';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#6b7280';
                }}
              >
                ×
              </button>
            </div>

            <div style={{ maxHeight: '60vh', overflow: 'auto' }}>
              {form.updatehistory && form.updatehistory.length > 0 ? (
                form.updatehistory.map((entry, index) => (
                  <div key={index} style={{
                    marginBottom: '24px',
                    padding: '20px',
                    backgroundColor: '#f9fafb',
                    borderRadius: '12px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '12px'
                    }}>
                      <div>
                        <h3 style={{
                          margin: '0 0 4px 0',
                          fontSize: '16px',
                          fontWeight: 600,
                          color: '#1f2937'
                        }}>
                          {entry.action}
                        </h3>
                        <p style={{
                          margin: '0 0 4px 0',
                          fontSize: '14px',
                          color: '#6b7280'
                        }}>
                          {entry.who}
                        </p>
                        <p style={{
                          margin: 0,
                          fontSize: '12px',
                          color: '#9ca3af'
                        }}>
                          {new Date(entry.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>

                    {entry.modifications && entry.modifications.length > 0 && (
                      <div style={{ marginTop: '16px' }}>
                        <h4 style={{
                          margin: '0 0 12px 0',
                          fontSize: '14px',
                          fontWeight: 600,
                          color: '#374151'
                        }}>
                          Changes Made:
                        </h4>
                        {entry.modifications.map((mod, modIndex) => {
                          const modKey = `${index}-${modIndex}`;
                          const isExpanded = expandedModifications[modKey];
                          const hasComplexData = (typeof mod.currentValue === 'object' && mod.currentValue !== null) || 
                                               (typeof mod.newValue === 'object' && mod.newValue !== null);
                          
                          return (
                            <div key={modIndex} style={{
                              marginBottom: '12px',
                              padding: '12px',
                              backgroundColor: 'white',
                              borderRadius: '8px',
                              border: '1px solid #e5e7eb'
                            }}>
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '8px'
                              }}>
                                <div style={{
                                  fontWeight: 600,
                                  color: '#1f2937',
                                  fontSize: '14px'
                                }}>
                                  {formatFieldName(mod.field)}
                                  {mod.description && (
                                    <span style={{
                                      fontWeight: 400,
                                      color: '#6b7280',
                                      marginLeft: '8px'
                                    }}>
                                      - {mod.description}
                                    </span>
                                  )}
                                </div>
                                {hasComplexData && (
                                  <button
                                    onClick={() => setExpandedModifications(prev => ({
                                      ...prev,
                                      [modKey]: !prev[modKey]
                                    }))}
                                    style={{
                                      background: 'none',
                                      border: '1px solid #d1d5db',
                                      borderRadius: '4px',
                                      padding: '4px 8px',
                                      fontSize: '12px',
                                      cursor: 'pointer',
                                      color: '#6b7280',
                                      transition: 'all 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.backgroundColor = '#f3f4f6';
                                      e.target.style.borderColor = '#9ca3af';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.backgroundColor = 'transparent';
                                      e.target.style.borderColor = '#d1d5db';
                                    }}
                                  >
                                    {isExpanded ? 'Hide Details' : 'Show Details'}
                                  </button>
                                )}
                              </div>
                            
                            {mod.currentValue !== undefined && (
                              <div style={{ marginBottom: '8px' }}>
                                <div style={{
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  color: '#dc2626',
                                  marginBottom: '4px'
                                }}>
                                  Previous:
                                </div>
                                <div style={{
                                  fontSize: '13px',
                                  color: '#374151',
                                  backgroundColor: '#fef2f2',
                                  padding: '8px',
                                  borderRadius: '4px',
                                  border: '1px solid #fecaca',
                                  wordBreak: 'break-word'
                                }}>
                                  {formatFieldValue(mod.currentValue, mod.field, taxonomyItems, concentrations)}
                                  {isExpanded && renderComplexObject(mod.currentValue, mod.field, taxonomyItems, concentrations)}
                                </div>
                              </div>
                            )}
                            
                            {mod.newValue !== undefined && (
                              <div>
                                <div style={{
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  color: '#059669',
                                  marginBottom: '4px'
                                }}>
                                  New:
                                </div>
                                <div style={{
                                  fontSize: '13px',
                                  color: '#374151',
                                  backgroundColor: '#f0fdf4',
                                  padding: '8px',
                                  borderRadius: '4px',
                                  border: '1px solid #bbf7d0',
                                  wordBreak: 'break-word'
                                }}>
                                  {formatFieldValue(mod.newValue, mod.field, taxonomyItems, concentrations)}
                                  {isExpanded && renderComplexObject(mod.newValue, mod.field, taxonomyItems, concentrations)}
                                </div>
                              </div>
                            )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '40px',
                  color: '#6b7280'
                }}>
                  No revision history available
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Sticky/Floating Submit Button */}
      {isFormEditable && (
        <div style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: '8px'
        }}>
          {/* Error messages above button */}
          {waiverId && !hasUnsavedChanges && (
            <div style={{
              background: '#fff',
              padding: '8px 16px',
              borderRadius: '8px',
              color: '#6b7280',
              fontSize: '13px',
              fontStyle: 'italic',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb'
            }}>
              No changes detected
            </div>
          )}
          {!waiverId && (!isStudentRole || !validEnrollment) && roleValidationError && (
            <div style={{
              background: '#fff',
              padding: '8px 16px',
              borderRadius: '8px',
              color: '#ef4444',
              fontSize: '13px',
              fontWeight: 500,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              border: '1px solid #fecaca',
              maxWidth: '300px'
            }}>
              {roleValidationError}
            </div>
          )}
          
          {/* Submit Button */}
          <button 
            type="button" 
            disabled={submitting || (waiverId && !hasUnsavedChanges) || (!waiverId && (!isStudentRole || !validEnrollment))} 
            onClick={handleSubmit}
            style={{ 
              background: (waiverId && !hasUnsavedChanges) || (!waiverId && (!isStudentRole || !validEnrollment))
                ? '#9ca3af' 
                : 'linear-gradient(135deg, #10b981 0%, #059669 100%)', 
              color: 'white', 
              padding: '16px 48px', 
              border: 'none', 
              borderRadius: '16px', 
              fontWeight: 700, 
              fontSize: '18px', 
              cursor: (waiverId && !hasUnsavedChanges) || (!waiverId && (!isStudentRole || !validEnrollment)) ? 'not-allowed' : 'pointer', 
              letterSpacing: 1,
              boxShadow: (waiverId && !hasUnsavedChanges) || (!waiverId && (!isStudentRole || !validEnrollment))
                ? 'none' 
                : '0 8px 25px rgba(16, 185, 129, 0.3)',
              transition: 'all 0.3s ease',
              transform: (waiverId && !hasUnsavedChanges) || (!waiverId && (!isStudentRole || !validEnrollment))
                ? 'none' 
                : 'translateY(0)',
              minWidth: '200px',
              position: 'relative'
            }}
            onMouseEnter={(e) => {
              if (!((waiverId && !hasUnsavedChanges) || (!waiverId && (!isStudentRole || !validEnrollment)))) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 12px 35px rgba(16, 185, 129, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              if (!((waiverId && !hasUnsavedChanges) || (!waiverId && (!isStudentRole || !validEnrollment)))) {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 8px 25px rgba(16, 185, 129, 0.3)';
              }
            }}
          >
            {submitting ? (waiverId ? 'Updating...' : 'Submitting...') : (waiverId ? 'UPDATE' : 'SUBMIT')}
          </button>
        </div>
      )}
    </div>
  );
};

export default RequestFEHoursByExternalActivity; 
